import { Input } from '../input/Keyboard.js';
import { DragYaw } from '../input/DragYaw.js';
import { Player } from '../actors/Player.js';
import { CameraRig } from '../actors/CameraRig.js';
import { EnemyManager } from '../actors/EnemyManager.js';
import { Spawner } from '../actors/Spawner.js';
import { SpriteEnemyRenderer } from '../actors/renderers/SpriteEnemyRenderer.js';
import { RigEnemyRenderer } from '../actors/renderers/RigEnemyRenderer.js';
import { BRAIN, PED_TUNING, STATE } from '../actors/ai.js';
import { attachFactionCombat, createPopulation } from './Population.js';
import { createWanted } from './Wanted.js';
import { createMissions } from './Missions.js';
import { ACTOR_DEPTH_BIAS, cameraDepthBias } from '../render/Billboards.js';
import { MARKER, MissionMarkerBatch } from '../render/MissionMarkers.js';
import { ShadowBlobPool } from '../render/ShadowBlobs.js';
import { createTracerPool } from '../render/Tracers.js';
import { createBloodPool } from '../render/Blood.js';
import { CollisionGrid } from '../world/CollisionGrid.js';
import { CarFleet } from '../vehicles/CarFleet.js';
import { buildLaneGraph } from '../traffic/LaneGraph.js';
import { Traffic } from '../traffic/Traffic.js';
import { createDriving } from './Driving.js';
import { createCarjack } from './Carjack.js';
import { VEHICLE, createVehicleDamage } from './VehicleDamage.js';
import { VehicleFxBatch } from '../render/VehicleFx.js';
import { createInteriors } from './Interiors.js';
import { createWallet } from './Wallet.js';
import { createStamina } from './Stamina.js';
import { createShops } from './Shops.js';
import { createWeapons } from './Weapons.js';
import { BREAK, createBreakables } from './Breakables.js';
import { createSpeech, SPEECH } from './Speech.js';
import { SpeechBubbleBatch } from '../render/SpeechBubbles.js';
import { DRONE, createDrone } from './Drone.js';
import { createPhone } from './Phone.js';
import { createGameAudio } from './GameAudio.js';
import { createRadio } from './Radio.js';
import { createSfx } from '../audio/Sfx.js';
import { DroneMesh } from '../render/DroneMesh.js';
import { loadPlayerSprite, PLAYER_ASPECT, PLAYER_HEIGHT } from '../assets/playerSprite.js';
import { streamRng } from '../math/rng.js';
import { ACCURACY, newShot, rollShot } from './accuracy.js';

/**
 * @file THE GAME LOOP GLUE. `createGameplay` takes an engine and a level and
 * returns one system that owns everything a playable session needs: input, the
 * player, the camera rig, the enemy manager + spawner, BOTH enemy renderers,
 * contact shadows, the auto-attack, the speech bubbles (`systems/Speech.js` +
 * `render/SpeechBubbles.js`, opt-in on the `dialogue` option and silent without
 * it), and every bus channel the HUD listens on.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE SYSTEM, ONE FIXED STEP, ONE ORDER
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 * stamina -> player|car -> camera -> traffic -> breakables -> vehicle damage
 *         -> enemies -> spawner -> auto-attack -> tracers -> blob shadows
 *         -> enemy renderers -> input.update()
 * ```
 * The order is load-bearing:
 *  • the stamina bar is spent and `player.speedMul` is written IMMEDIATELY
 *    before the mover reads it, because the multiplier is spent by the very
 *    step it gates — a frame of lag there is a sprint that starts and stops one
 *    step after the key;
 *  • the camera follows the player's NEW position, so the frame is never a step
 *    behind;
 *  • traffic runs on that same new position, so a car brakes for where the
 *    player IS rather than where they were;
 *  • the breakables and the vehicle damage run on the car's NEW position, so an
 *    impact is spent on the step it was measured — and both go before the
 *    enemies, so a car that explodes has already dealt its damage when the
 *    manager gives those slots their turn;
 *  • enemies steer toward it too, so "the mob converges" is true on the step it
 *    happens;
 *  • the renderer syncs LAST, from finished state, so nothing is drawn mid-move;
 *  • `input.update()` is dead last — it is a frame FLUSH of the edge-triggered
 *    key sets, and clearing them earlier eats presses that arrived between steps
 *    (see `input/Keyboard.js` rule 1).
 *
 * ⚠ Step 1 is `player.update` OR `driving.update`, never both: in a car the
 * player is CARGO (`Player#carry`) and WASD belongs to the car. Everything
 * downstream still reads `player.position`, which is why nothing downstream had
 * to learn what a vehicle is. There is now a THIRD owner of that position for
 * ~1.6 s at a time — `systems/Carjack.js`, which walks the player to the door
 * while a sequence runs (`carjack.locked`). Same rule: exactly one mover.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * F IS A SEQUENCE, NOT A FUNCTION CALL
 * ─────────────────────────────────────────────────────────────────────────────
 * F goes to `systems/Carjack.js`, never straight at `Driving.toggle()`. One
 * state machine covers every way in and out of a car — the door swing, the
 * driver being dragged out of somebody else's, the slide in, the door shut —
 * and it drives `Driving`'s two-phase enter/exit seam from the outside. It is
 * built for any level with roads; with no faction table there are simply never
 * any occupants and it is the door choreography on an empty car.
 *
 * F is ALSO the building door, so `systems/Interiors.js` is offered the press
 * FIRST and the vehicle machine takes it on a false. There is no contest: a
 * door ring is never inside a car, and each machine asks the other whether a
 * sequence is running before it claims anything. That system brings one more
 * thing with it — THE FOCUS. A player under the city is still standing on the
 * pavement as far as traffic, the population, the spawner, the wanted level and
 * the minimap are concerned (`interiors.focus`, step 1a), or the street empties
 * behind them while they shop and is still empty when they come out.
 *
 * F IS ALSO THE TILL (`systems/Shops.js`), offered BETWEEN the two: a room's
 * crowd exists only while the player is standing in it, and the counter they
 * walk up to sells exactly one thing. The order `interiors → shops → carjack` is
 * written out explicitly in the key handler rather than left to fall out of the
 * gates, and the shop's own step runs at 2a — AFTER `manager.update`, because
 * the manager writes every actor's Y from the OUTDOOR terrain and the pin that
 * puts a customer back on the room's floor at −100 has to be the last word. The
 * money is `systems/Wallet.js`, built unconditionally: missions pay into it, the
 * till spends out of it, and nothing else holds a copy of the figure.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TRAFFIC IS OPT-IN BY DATA, NOT BY FLAG
 * ─────────────────────────────────────────────────────────────────────────────
 * A level that publishes `map.roads` gets a lane graph, a `CarFleet`, an AI
 * traffic director and player driving; one that does not (every dev scene) gets
 * none of it and pays nothing. The whole subsystem draws from its own
 * `'traffic'` / `'traffic.turns'` streams and never touches the manager's, so
 * hero mode's pinned rng sequence is untouched with traffic on or off.
 *
 * The auto-attack is SUPPRESSED while driving. There is no drive-by yet, and a
 * hitscan that fires out of a moving car with no animation reads as a bug.
 *
 * The camera yaw has two spellings and they are the SAME control: hold Q/E, or
 * left-click-drag the canvas (`input/DragYaw.js`). Both end up in
 * `CameraRig#rotateYawBy` inside step 1, so the frame only ever sees one yaw.
 * Shift+Q/E still snaps 90 degrees.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SHOT: HITSCAN, WITH A MISS ROLL AND A VISIBLE BULLET
 * ─────────────────────────────────────────────────────────────────────────────
 * The auto-attack is still instantaneous — damage lands on the step the trigger
 * is pulled, and there is deliberately no projectile actor and no projectile
 * collision. Two things sit on top of that, and neither one changes the fact:
 *
 *  • `systems/accuracy.js` rolls a distance-scaled MISS before the damage. A
 *    miss applies nothing and displaces the impact point sideways, so the
 *    feedback shows the round going wide instead of silently deleting a tick of
 *    damage. It draws from a dedicated `'aim'` stream (rule 4) — never the
 *    manager's, whose draw order is pinned by a golden checksum.
 *  • `render/Tracers.js` FLIES a short streak from the muzzle to that impact
 *    point at ~160 m/s. It is animation over an outcome that already happened;
 *    nothing downstream waits for it.
 *
 * A connect also throws blood (`render/Blood.js`): droplets that arc to the
 * ground and leave splats that fade out after ~3 s. A miss never bleeds.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO RENDERERS, NO TOGGLE
 * ─────────────────────────────────────────────────────────────────────────────
 * A creature is EITHER 2D art OR a 3D model — never both. That was an owner
 * decision, and it deleted the old A/B toggle along with the bus channels and
 * the HUD control that drove it: with one backend per type there is nothing to
 * switch BETWEEN.
 *
 * So both renderers stay alive and both sync every frame, but the routing is
 * decided ONCE, at construction, from what each type actually carries:
 * a type with a rig draws through `RigEnemyRenderer`, everything else through
 * `SpriteEnemyRenderer`. The split is expressed as a per-type mask on each
 * renderer (`setTypeMask`) — the cost of the unused half is one empty linear
 * pass over the pool, and mixing sprite creatures with model creatures in one
 * level costs nothing extra.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BUS CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 * | channel                  | dir | payload                              |
 * |--------------------------|-----|--------------------------------------|
 * | `player:damage`          | in  | `{amount, from?, type?}` (enemies emit)|
 * | `player:hp`              | out | `{hp, max, armour, maxArmour}` on every change |
 * | `player:cash`            | out | `{cash}` — `systems/Wallet.js` publishes it |
 * | `shop:bought`            | out | `{kind, item, price, cash}` — the till |
 * | `game:kills`             | out | `number` — running total              |
 * | `player:respawn`         | out | `{x, z}`                              |
 * | `player:wasted`          | out | `{x, z}` — the death splash's cue     |
 * | `player:vehicle`         | out | `{in: boolean}` on every enter/exit    |
 * | `hud:blips`              | out | `{player:{x,z,yaw}, blips:[{x,z,kind}], markers:[…]}` at ~8 Hz |
 * | `crime`                  | out | `{type, x, z, faction, victim}` — factions only |
 * | `wanted:level`           | out | `{level: 0..5, flash}` — factions only, ON CHANGE |
 * | `car:wrecked`            | in  | `{handle, x, y, z, byPlayer}` — `systems/VehicleDamage.js` emits; gameplay RETIRES the slot |
 * | `car:despawn`            | in  | `{handle, x, y, z}` — same emitter, once, a minute after a wreck burned; gameplay REMOVES the car |
 * | `car:ranover`            | in  | `{x, y, z, dx, dz, victim, byPlayer, fatal}` — same emitter, once per victim per hit; gameplay THROWS THE BLOOD and nothing else |
 * | `player:ammo`            | out | `{id, label, clip, clipSize, reserve, reserveMax, reloading, reloadT, reloadTime, hot}` — ⚠ published by `systems/Weapons.js`, NOT by this file |
 * | `weapon:reload`          | in  | `undefined` — the HUD's reload tap; `Weapons.js` subscribes itself |
 * | `weapon:hot`             | in  | `undefined` — the HUD's safe/hot tap; same, and the same reason |
 * | `drone:state`            | out | `{state, stateName, pending, x, y, z}` on every TRANSITION — ⚠ published by `systems/Drone.js`, NOT by this file |
 * | `drone:delivered`        | out | `{rounds, x, z}` — same emitter, once per delivery |
 * | `radio:track`            | out | `{stationId, stationName, trackId, title, file, offset, dur}` on every track START or RESUME — ⚠ published by `systems/Radio.js`, NOT by this file |
 *
 * ⚠ THE TWO `drone:*` ROWS ARE `systems/Drone.js`'s AND `radio:track` IS
 * `systems/Radio.js`'s, and they are listed here for the reason the `weapon:*`
 * pair is: they are part of the contract a HUD (or a review scene) sees, not
 * because anything in this file handles them. The same sole-publisher rule
 * applies to all three — `RadioHud.jsx` and `GameAudio.js` are both subscribers
 * on that channel and neither may ever answer on it. Note also what is NOT a
 * row: the radio needed no "the player got into a car" signal of its own,
 * because `player:vehicle` above already carried it. Note likewise the drone's whole
 * TRIGGER is a subscription to `player:ammo` living inside `Drone.js`, which is
 * why the gun learned nothing about the drone and why no channel was invented
 * for "the player is running low".
 *
 * ⚠ THE THREE `weapon:*` / `player:ammo` ROWS ARE `systems/Weapons.js`'s, NOT THIS FILE'S, and the
 * ammo one in particular: it is the SOLE publisher of that channel and
 * `tests/weapons.test.mjs` section (h) greps THIS source to prove gameplay never
 * emits there. A purchase happens with the fixed step frozen at 0 (an open shop
 * panel — `docs/shops.md`), so a step-end emitter here would never fire for the
 * one event the player is looking straight at. Gameplay's whole involvement is
 * constructing the model, ticking its clock, spending its rounds and routing two
 * keys at it; the channels are its own. Both `weapon:*` rows are listed because
 * they are part of the contract a HUD sees, not because anything here handles
 * them.
 *
 * ⚠ `'player:damage'` IS REROUTED IN A CAR. While `driving.inCar` the amount
 * goes to the CAR (`VehicleDamage#damage`, scaled by `VEHICLE.bulletToCar`) and
 * the player takes NOTHING — so a player under fire in a car is killed by the
 * car exploding, not by the bullets. See the handler for the full note; on foot
 * and in every level without roads it behaves exactly as it always did.
 *
 * `systems/Missions.js` owns four more (`mission:list`, `mission:objective`,
 * `mission:cutscene`, `mission:event`) and consumes three (`mission:track`,
 * `mission:cutscene:end`, `player:respawn`) — see its own header. Gameplay's
 * only involvement is building it, ticking it and drawing its markers.
 *
 * `'crime'` types: `shot_`/`killed_` × `civilian`/`gang`/`police`, plus
 * `carjack` (the only one that is not damage — `systems/Carjack.js` emits it
 * through the same `Population#emitCrime` door, with the victim's position and
 * faction, once per completed drag).
 *
 * ⚠ `systems/GameAudio.js` ADDS NO ROW TO THIS TABLE, and that is worth stating
 * because it looks like a system that would. It is a pure SUBSCRIBER: fifteen
 * channels this file and its neighbours already published — several of which had
 * no consumer at all, which is exactly why they were free — plus five call sites
 * below that are one guarded `audio?.…` line each. Nothing was invented for
 * sound, and nothing may be: a second publisher on any of these is the
 * `'hud:prompt'` flicker rule. It is also the only system here with a `render`
 * hook as well as an `update`; see its header, and `render()` below.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FACTIONS ARE OPT-IN, AND THE WHOLE CITY HANGS OFF ONE OPTION
 * ─────────────────────────────────────────────────────────────────────────────
 * Pass `factions` (a `game/factions.js#createFactionRules()` table) and three
 * things change together: the manager runs `targeting: 'factions'`, the ambient
 * source becomes `systems/Population.js` instead of the hostile `Spawner`, and
 * the player's gun stops firing at everything that moves. Pass nothing — every
 * dev scene, every test — and not one line of it runs. The `Spawner` is still
 * built and still exported either way; the city simply stops ticking it.
 *
 * The player has exactly ONE trigger, and it is not one they pull. The AUTO
 * attack only ever shoots something whose faction is hostile to the player
 * RIGHT NOW; with nobody provoked the gun is silent, which is what makes
 * walking down a street feel like walking down a street.
 *
 * ⚠ THAT IS STILL TRUE, BUT THE GUN IS NO LONGER JUST A CADENCE. `systems/
 * Weapons.js` is built when a `weapons` TABLE is injected, and it gives the
 * trigger three things to run out of or be stopped by: a CLIP (a round is spent
 * per shot FIRED, hit or miss, and the clip going empty starts the reload on
 * that same step), a RELOAD clock (R, or `'weapon:reload'`), and a SAFE stance
 * (X, or `'weapon:hot'`) that holds the gun down while a hostile is standing in
 * front of the player. So the player still cannot CHOOSE a target and still
 * cannot start a fight with the trigger — the three doors below are unchanged —
 * but they CAN now choose not to shoot, and they can run dry. Without the table
 * (every dev scene, every test, `tests/hero.test.mjs`) `weapons` is null, the
 * cadence falls back to {@link FIRE.interval} and every line of it is inert:
 * that is what keeps the golden checksum valid, the `factions` idiom exactly.
 *
 * ⚠ THE DELIBERATE SHOT IS DELETED, NOT DISABLED (owner decision, 2026-08-07).
 * Holding SPACE on foot used to fire at the nearest actor of ANY faction in a
 * 120 degree cone, and that was how the player STARTED trouble. It went because
 * it was doing the opposite of its job: it turned the hostile filter OFF, so
 * during a firefight the nearest thing in the cone was routinely a fleeing
 * bystander rather than one of the shooters (who plant OUTSIDE the cone and,
 * before {@link FIRE.range} moved, outside the range too). Space is now only
 * the handbrake. A manual shot is BACKLOG, and when it returns it must PREFER
 * hostiles and only fall through to a bystander when the cone holds none.
 *
 * ⚠ THE CONSEQUENCE, WHICH IS A DESIGN DECISION AND NOT AN OVERSIGHT: the
 * player can no longer provoke anybody directly, so an ambient gang member on a
 * corner is unattackable until something else turns his crew. The three doors
 * that still open are `systems/Carjack.js` (a completed drag provokes a crew
 * and bills a crime either way), `systems/Wanted.js` (the police half), and a
 * mission `role: 'hostile'` spawn, which provokes its faction every step.
 *
 * `'crime'` is the seam `systems/Wanted.js` consumes, so combat never has to
 * learn what a wanted level is. Its `type` is `shot_` or `killed_` followed by
 * the victim's side — `civilian`, `gang` or `police` — e.g. `shot_civilian`,
 * `killed_gang`. The payload carries where it happened, the victim's faction id
 * and its manager slot, and it fires ONLY for damage the PLAYER dealt: gangs
 * shooting each other is not a crime.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE WANTED LEVEL AND THE WASTED LOOP
 * ─────────────────────────────────────────────────────────────────────────────
 * `systems/Wanted.js` is built alongside the population and given the same three
 * directors; it owns the heat, `'wanted:level'`, `FactionState#setPoliceHostile`
 * and every police unit in the world. Gameplay's only jobs are to tick it after
 * the population (so a unit spawned this step sees a settled world) and to call
 * `wanted.reset()` on the respawn.
 *
 * DEATH IS A SEQUENCE with three beats, and the order is the whole contract:
 * `'player:wasted'` fires on the step `player.dead` first becomes true (with the
 * place they fell), the player is frozen for {@link FIRE.respawnDelay} seconds —
 * no movement, no gun, no F — and then `'player:respawn'` fires with the plaza.
 * `ui/hud/Wasted.jsx` is driven by exactly that pair; nothing about the splash
 * is on a timer of its own.
 *
 * ⚠ The respawn CLEAR is faction-aware. In hero mode it despawns everything
 * within `respawnClearR` of the spawn, as it always did. In a faction city it
 * despawns only what is still hostile to the player — and since `wanted.reset()`
 * has already cleared the heat and every provocation, that is nobody. The
 * ambient street the player wakes up on is the one they left.
 *
 * ⚠ `hud:blips` REUSES its payload, its array and its entry objects — it fires
 * eight times a second forever and must not litter. A subscriber that keeps the
 * reference will watch it mutate under it; copy what you need inside the
 * handler (`ui/hud/Minimap.jsx` copies into flat arrays). Every other channel
 * above allocates a fresh payload and can be retained safely. THE MARKER ARRAY
 * IS THE MISSION DIRECTOR'S OWN REUSED ONE, so the rule applies twice over
 * there.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MISSIONS ARE OPT-IN BY CONTENT, LIKE FACTIONS
 * ─────────────────────────────────────────────────────────────────────────────
 * Pass `missions` (a `game/missions/index.js#createMissionScript()`) ON TOP of a
 * faction table and `systems/Missions.js` is built: the giver rings, the
 * objective machine, the escort, the cutscene handshake and the marker feed.
 * Without it nothing here runs and the marker batch is never even constructed.
 * It requires factions because every mission actor needs an allegiance, and a
 * teammate who cannot tell your enemies from your friends is not a teammate.
 *
 * ⚠ THE TELEPORT IS GAMEPLAY'S, NOT THE DIRECTOR'S. `Missions.js` imports no
 * three.js, so "move the player to the conflict zone" arrives as a callback.
 * It has one case worth knowing: a player who is DRIVING is moved CAR AND ALL
 * (the body is a plain record `Driving` integrates, so writing it is legal),
 * because taking the car away mid-cutscene and leaving it on the far side of
 * the island is worse than either alternative.
 *
 * @module engine/systems/Gameplay
 */

/** @typedef {import('../types.js').Level} Level */
/** @typedef {import('../types.js').EnemyType} EnemyType */
/** @typedef {import('../actors/Spawner.js').SpawnRule} SpawnRule */

/** Auto-attack tuning — the plan's numbers, in one place. */
const FIRE = Object.freeze({
  interval: 0.28,
  /**
   * ⚠ THIS NUMBER IS DERIVED FROM THE CAST'S WEAPONS, NOT FROM TASTE.
   *
   * A ranged NPC does not walk into contact: it closes to `fire.range * 0.9`
   * and PLANTS there (`EnemyManager#update`, the `planted` branch damps its
   * velocity to 0.35). So the standoff distances the player is actually shot
   * from are gang 9.9 m, `police_officer` 10.8, `swat` 11.7, `tank` 13.5 —
   * and at the shipped 9 m EVERY ONE OF THEM parked outside the player's reach
   * and shot at them forever. The auto-attack never acquired anything, the
   * player could not fire back without manually walking in, and it read as a
   * gun that did not work (owner playtest, 2026-08-07).
   *
   * 14 clears the worst of them (the tank) by 0.5 m. Raise a def's
   * `fire.range` past 15 and this MUST move with it, or that type is
   * unanswerable again.
   */
  range: 14,
  /**
   * HALF of the targeting arc, measured from the camera-forward direction.
   * 180 = full 360° targeting. Anything narrower plays badly in a horde game:
   * pursuers approach from behind the player (screen-bottom), so a
   * front-facing arc leaves them unshootable — verified in the city demo.
   */
  arcDeg: 180,
  /**
   * ⚠ THE ONE THING THAT KEEPS THE GUN INSIDE THE WORLD IT CAN SEE.
   *
   * `pickTarget`'s range test is XZ-ONLY, and a shop interior is a real room
   * 100 m UNDER the island's SW quadrant (`systems/Interiors.js`) — so without
   * a Y test the player standing in a room can shoot, and be shot at by, a
   * hostile on the street directly above the rock. That was survivable at the
   * old 9 m because the interior FOCUS keeps ambient sources at the shopfront;
   * at 14 m a cop planted at 10.8 m is now inside the band, so the guard the
   * docs always named (`docs/interiors.md`: "the fix is a Y test in
   * `pickTarget`") is here.
   *
   * 20 is generous ON PURPOSE — it must never reject a legitimate target on a
   * slope. `terrain.hillAmp` 12 over `hillScale` 130 is ~0.58 of gradient at
   * its steepest, i.e. up to ~8 m of relief across a 14 m radius, so 20 leaves
   * 2.5x of headroom above the worst hillside while still sitting 5x under the
   * 100 m an interior is buried by.
   */
  maxDy: 20,
  damage: 12,
  /**
   * Seconds between `'player:wasted'` and `'player:respawn'` — the length of
   * the death splash, and the length of time the player is frozen. 3 s reads as
   * a beat rather than a load screen; the HUD has no clock of its own, so this
   * is THE number.
   */
  respawnDelay: 3,
  /** Enemies inside this radius of the spawn are despawned on respawn. */
  respawnClearR: 26,
  /** HARD CAP on a tracer's flight, seconds — see `render/Tracers.js`. */
  tracerLife: 0.5,
  tracerWidth: 0.075,
  tracerY: 0.62,
  /**
   * Pool size. 16 covered a lone hero; a border firefight has two crews of four
   * trading shots THROUGH the player's own auto-attack, and a tracer that never
   * launches because the ring wrapped reads as a gun that jammed. Still one
   * instanced draw either way.
   */
  tracers: 28,
  /** Bullet streak speed (m/s) and length (m). 14 m of range = ~88 ms. */
  tracerSpeed: 160,
  tracerTrail: 2.6,
  /**
   * The miss model (`systems/accuracy.js`). Tightened from the shipped default
   * because the fire interval is 0.28 s: at the stock 40 % edge miss the
   * auto-attack reads as broken rather than as a long shot.
   *
   * ⚠ The miss ramps on `dist / FIRE.range`, so widening the range ALSO made
   * every old distance more accurate — a 9 m shot used to sit at the ragged
   * edge of the falloff and now sits at 0.64 of it. That is a straight buff
   * riding on the range change; if the gun ever feels too good, this is the
   * dial, not {@link FIRE.range} (which is pinned to the cast's weapons).
   */
  accuracy: Object.freeze({ ...ACCURACY, pointBlank: 2.5, maxMiss: 0.3 }),
  /** Where a MISS visually lands, metres above the ground it scatters onto. */
  missY: 0.3,
  /**
   * The manager's own miss model, opted into ONLY in faction mode. Gang and
   * police weapons reach 11–12 m, so the band is wider than the hero's and the
   * edge miss is higher: a street fight in which nobody ever misses is over in
   * two seconds and reads as a bug in the other direction.
   *
   * It draws from the manager's dedicated `'aim'` stream, never the population
   * one — which is why turning it on cannot move `tests/hero.test.mjs`.
   */
  enemyAccuracy: Object.freeze({ ...ACCURACY, pointBlank: 3, maxMiss: 0.35 }),
});

/**
 * HUD minimap feed. Read-only: this publisher never draws a random number and
 * never touches actor state, so hero mode's pinned rng sequence is untouched.
 */
const BLIPS = Object.freeze({
  /**
   * Seconds between emits. 8 Hz is deliberately UNDER the minimap's 20 Hz
   * redraw: a blip that lags 125 ms at 1.4 px/m is off by ~0.6 px, and paying
   * for a per-frame census of the pool to fix that would be absurd.
   */
  interval: 1 / 8,
  /** Hard cap on entries, NEAREST first — the far ones are off the window. */
  max: 64,
  /** Below this speed² the player's map heading holds (see `EnemyManager`'s
   *  sticky facing byte: a chevron must not snap north when you stop). */
  moveEps: 0.04,
});

/**
 * The ground rings that say "this door is a door" — drawn through a second
 * {@link MissionMarkerBatch}, so a door and an objective are the same shape and
 * obey the same rule. Metres and LINEAR RGB.
 */
const DOORS = Object.freeze({
  /** Instance cap. The batch's own default; stated here because the near-set
   *  window below is sized from it. */
  max: 16,
  /** Draw radius around the player. Roughly a block of shopfronts — far enough
   *  that you see the parade coming, near enough that the rings are never a
   *  carpet of circles across the district. */
  showR: 45,
  /**
   * Ring radius. It is NOT `DOOR.reach` (2.2 m): the reach is a generous grab
   * radius so F never feels finicky, and a ring that big would swallow the
   * pavement and overlap its neighbours. 0.9 m is the mat you stand on.
   */
  radius: 0.9,
  /**
   * Cool white — deliberately away from `MARKER.tints.giver`'s gold, and cooler
   * and dimmer than `objective`'s near-white: a door is always there, and a
   * permanent fixture must never out-shout the thing you are being sent to.
   * @type {[number, number, number]}
   */
  tint: /** @type {[number, number, number]} */ ([0.30, 0.40, 0.50]),
  /** Per-instance alpha before the batch's shared pulse. */
  alpha: 0.55,
});

/**
 * Blip kind from an authored enemy def's id. The def id prefix IS the faction —
 * that is the whole naming convention in `src/game/enemies/`, so no def needs a
 * HUD field. Unknown ids fall back to `civilian` (grey), which is the reading
 * that misleads least.
 *
 * @param {string} id
 * @returns {'police'|'gang_red'|'gang_green'|'gang_purple'|'civilian'|'car'}
 */
function blipKindForId(id) {
  const s = String(id ?? '').toLowerCase();
  if (s.startsWith('gang_red')) return 'gang_red';
  if (s.startsWith('gang_green')) return 'gang_green';
  if (s.startsWith('gang_purple')) return 'gang_purple';
  // `tank` is the wanted-level-5 army response — it reads as law, not gang.
  if (s.startsWith('police') || s.startsWith('swat') || s.startsWith('tank')) return 'police';
  if (s.startsWith('npc')) return 'civilian';
  return 'civilian';
}

/**
 * @param {import('../core/Engine.js').Engine} engine
 * @param {Object} o
 * @param {EnemyType[]} o.types Built by `actors/enemyTypes.js` (or wave D's
 *   editor). `typeIdx` in the spawn rules indexes THIS array.
 * @param {SpawnRule[]} [o.spawnRules] Defaults to one rule per type, 40 alive
 *   each, on the standard 25–45 m ring.
 * @param {Object} [o.playerOpts] Forwarded to {@link Player}. Pass `texture` +
 *   `aspect` from `loadPlayerSprite()` when you already awaited it; otherwise
 *   gameplay loads the chibi itself and hot-swaps the player once it decodes.
 * @param {Object} [o.cameraOpts] Forwarded to {@link CameraRig}.
 * @param {Level|null} [o.level] Defaults to `engine.level` — pass it explicitly
 *   when installing from INSIDE a level builder (the engine has not assigned
 *   `engine.level` yet at that point).
 * @param {import('three/webgpu').Object3D|null} [o.parent] Where the actor visuals live. MUST be
 *   an identity transform (see `render/Billboards.js`). Defaults to
 *   `level.root`, falling back to `engine.scene`.
 * @param {any} [o.collision] Overrides `level.collision`; otherwise a
 *   `CollisionGrid` is built from `level.colliders`.
 * @param {number} [o.cap=512] Enemy slot cap.
 * @param {Partial<import('../actors/ai.js').AiTuning>|null} [o.tune] Overrides
 *   `AI_DEFAULTS` for the whole population (aggro bands, windup, knockback…).
 * @param {string|number} [o.seed='gameplay'] Drives spawns + wander. Same seed,
 *   same fight.
 * @param {boolean} [o.traffic=true] Build the lane graph + car fleet when the
 *   level publishes `map.roads`. Pass `false` for a level that wants an empty
 *   city (a bestiary, a screenshot).
 * @param {import('./Population.js').FactionRules|null} [o.factions] THE FACTION
 *   SWITCH — see the header. `game/factions.js#createFactionRules()`. Requires
 *   a level that publishes `map.districts`; without one the population director
 *   has no pavements to work with and gameplay falls back to the `Spawner`.
 * @param {import('./Missions.js').MissionScript|null} [o.missions] THE MISSION
 *   SCRIPT — `game/missions/index.js#createMissionScript()`. Requires
 *   `factions`; ignored without one (see the header).
 * @param {Object|null} [o.dialogue] THE
 *   LINE BANK — `game/dialogue.js#createDialogueScript()`, injected by the ROUTE
 *   the same way the faction table and the mission script are, because the
 *   engine never imports `src/game/`. Without it NOT ONE speech branch runs and
 *   gameplay is exactly what it was: no `Speech` director, no bubble batch, no
 *   draw call, nothing paid.
 * @param {{starting?: string, weapons?: readonly any[]}|null} [o.weapons] THE
 *   WEAPON TABLE — `game/weapons.js#createWeaponTable()`, injected by the ROUTE
 *   exactly like the faction table, the mission script and the line bank,
 *   because the engine never imports `src/game/`. Given one, `systems/
 *   Weapons.js` is built and the auto-attack acquires a clip, a reserve, a
 *   reload and a safety catch. Without it NOT ONE weapon branch runs: the
 *   cadence stays {@link FIRE.interval}, no round is ever spent, R and X are
 *   dead keys and nothing is published on `'player:ammo'` — which is why the
 *   hero golden checksum cannot move.
 * @param {Object|null} [o.sounds] THE SOUND TABLE —
 *   `game/sfx.js#createSoundTable()`, injected by the ROUTE exactly like the
 *   faction table, the mission script, the line bank and the weapon table,
 *   because the engine never imports `src/game/`. Given one, `engine/audio/
 *   Sfx.js` and `systems/GameAudio.js` are built and the game makes a noise.
 *   Without it NOT ONE audio branch runs: no facade, no `AudioContext`, no
 *   subscription, no poll and no call site — every one of them is `audio?.…`
 *   against a null — which is why the hero golden checksum cannot move.
 * @param {{base: string, stations: any[]}|null} [o.radio] THE RADIO TABLE —
 *   `game/radio.js#createRadioTable()`, the sixth injected content seam and
 *   injected for the same reason as the five above it. Given one, `systems/
 *   Radio.js` is built and a station is permanently on air; getting into a car
 *   opens a window onto it. Without it nothing is constructed, no track is ever
 *   picked, no die is drawn from its private stream and `'radio:track'` is never
 *   published — the `factions`/`weapons`/`sounds` discipline exactly, and the
 *   reason hero mode's golden checksum 1878938342 cannot move.
 * @param {{gain?: number, fade?: number}|null} [o.radioTune] `game/radio.js#
 *   RADIO_TUNE`, handed straight through to `GameAudio`. ⚠ A SECOND OPTION FOR
 *   TWO NUMBERS, on purpose: they are a MIX decision (how loud the radio sits
 *   under the gunfire, how long it takes to arrive), they live in `src/game/`
 *   beside the stations they are about, and the engine may not import that
 *   directory — so the alternative was a copy of 0.55/0.35 inside the engine
 *   that has to move whenever the authored pair does. See `GameAudio.js`'s own
 *   `radioTune` note.
 * @param {import('../types.js').Registry|null} [o.registry]
 * @returns {Object} A system: `{name, update(dt), dispose()}` plus live refs
 *   (`player`, `manager`, `spawner`, `rig`, `input`, `kills`).
 */
export function createGameplay(engine, o = {}) {
  const {
    types = [],
    spawnRules = null,
    playerOpts = {},
    cameraOpts = {},
    level: levelOpt = null,
    parent: parentOpt = null,
    collision: collisionOpt = null,
    cap = 512,
    tune = null,
    seed = 'gameplay',
    traffic: wantTraffic = true,
    factions: factionRules = null,
    missions: missionScript = null,
    dialogue: dialogueScript = null,
    weapons: weaponTable = null,
    sounds: soundTable = null,
    radio: radioTable = null,
    radioTune = null,
    registry = null,
  } = o;

  if (!engine) throw new Error('createGameplay: an engine is required');

  const level = levelOpt ?? engine.level ?? null;
  const parent = parentOpt ?? level?.root ?? engine.scene;
  const reg = registry ?? engine.registry ?? null;
  const bus = engine.bus;

  // ── collision ──────────────────────────────────────────────────────────────
  // Levels may hand us a live grid (`level.collision`) or just the collider
  // list (`level.colliders`, the types.js contract). Build one either way — the
  // player and 120 enemies all resolve against it every step.
  let ownedGrid = null;
  const collision =
    collisionOpt ??
    /** @type {any} */ (level)?.collision ??
    (level?.colliders?.length
      ? (ownedGrid = new CollisionGrid(
        level.bounds ?? { minX: -256, minZ: -256, maxX: 256, maxZ: 256 },
        4,
      ).addColliders(level.colliders))
      : null);

  const heightAt = typeof level?.heightAt === 'function' ? level.heightAt : () => 0;
  const probes = level?.probes ?? null;
  const spawn = level?.spawnPoints?.player ?? { x: 0, y: heightAt(0, 0), z: 0 };

  // ── input + camera ─────────────────────────────────────────────────────────
  const input = new Input();
  // Left-click-drag is the mouse spelling of Q/E — same yaw, same rig call, so
  // there is nothing to keep in sync between them. It banks its rotation and
  // the step below drains it (see `input/DragYaw.js`).
  const drag = new DragYaw(engine.canvas ?? null);
  const rig = new CameraRig(engine, {
    pitchDeg: engine.opts?.pitchDeg ?? 52,
    distance: engine.opts?.distance ?? 34,
    yaw: engine.yaw,
    bounds: level?.bounds ?? null,
    ...cameraOpts,
  });

  // ── player ─────────────────────────────────────────────────────────────────
  const levelStub = { heightAt, probes, collision };
  const playerArgs = {
    level: levelStub,
    input,
    getCameraYaw: () => rig.yaw,
    spawn: { x: spawn.x, y: spawn.y ?? heightAt(spawn.x, spawn.z), z: spawn.z },
    height: PLAYER_HEIGHT,
    ...playerOpts,
  };
  let player = new Player(playerArgs);
  parent.add(player.object);
  rig.snapTo(player.position);

  // No texture supplied: load the chibi and swap the sprite in when it decodes.
  // `SpriteNodeMaterial` bakes the texture into its node graph at construction,
  // so "swap" means rebuilding the Player — which is cheap and preserves state.
  if (!playerArgs.texture) {
    loadPlayerSprite()
      .then((res) => {
        if (disposed || !res?.texture) return;
        const old = player;
        const next = new Player({
          ...playerArgs,
          texture: res.texture,
          aspect: res.aspect ?? PLAYER_ASPECT,
          spawn: { x: old.position.x, y: old.position.y, z: old.position.z },
        });
        next.hp = old.hp;
        next.maxHp = old.maxHp;
        // The vest is STATE too: a player who bought armour and then had their
        // sprite decode under them must not be handed a fresh, bare one.
        next.armour = old.armour;
        next.maxArmour = old.maxArmour;
        parent.add(next.object);
        old.dispose();
        player = next;
        sys.player = next;
        // Driving/Carjack captured the actor at construction — repoint them
        // or they go on steering a disposed sprite (latent until a level
        // ships without a preloaded player texture).
        driving?.setPlayer(next);
        carjack?.setPlayer(next);
        // The door machine has one extra job here: a replacement built from
        // `playerArgs` arrives with the CITY's collision and the terrain's
        // height, so if the swap lands while the player is in a room it has to
        // put the interior environment straight back on. It does.
        interiors?.setPlayer(next);
        shops?.setPlayer(next);
        missions?.setPlayer(next);
        // ...and the LISTENER, which is `player.position` and nothing else. A
        // stale actor here freezes the ears wherever the sprite happened to
        // decode, and every pan and every distance in the game goes with them.
        audio?.setPlayer(next);
      })
      .catch((err) => console.warn('[gameplay] player sprite failed to load:', err?.message ?? err));
  }

  // ── enemies ────────────────────────────────────────────────────────────────
  const manager = new EnemyManager({ types, cap, seed, tune });
  const rules = spawnRules ?? types.map((t, i) => ({ typeIdx: i, weight: 1, maxAlive: 40 }));
  const spawner = new Spawner({
    manager,
    rules,
    rng: `${seed}:spawner`,
    collision,
    bounds: level?.bounds ?? null,
    heightAt,
    // The ring must sit INSIDE the aggro band or a standing player watches a
    // crowd mill around out of reach; the cull must stay well outside it or
    // enemies thrash in and out at the boundary.
    despawn: Math.max(70, manager.tune.deAggro * 1.6),
  });

  // ── factions ───────────────────────────────────────────────────────────────
  // Faction mode needs BOTH a rules table and a level that published its city
  // blocks: the pavement graph and the territory split are derived from
  // `map.districts`, and without them there is nothing for a population
  // director to direct. Anything else falls back to the hostile `Spawner`.
  /** @type {import('./Population.js').FactionRules|null} */
  const fac = factionRules && level?.map?.districts?.length ? factionRules : null;
  /** @type {ReturnType<typeof createPopulation>|null} Built after traffic. */
  let population = null;
  /**
   * @type {ReturnType<typeof createMissions>|null} Built LAST — it borrows the
   * population's pavements, the traffic's seats and the driving system's body.
   * Declared here so `manager.onDespawn` below can close over it.
   */
  let missions = null;
  /**
   * @type {ReturnType<typeof createSpeech>|null} THE TALKING, and its batch.
   * Both are built far below (they read the wanted level), but they are
   * DECLARED here for the same reason `missions` is: `manager.onDespawn` closes
   * over `speech`, and a slot can free before the constructor reaches them.
   */
  let speech = null;
  /** @type {SpeechBubbleBatch|null} The one draw call the lines cost. */
  let bubbles = null;
  if (fac) manager.accuracy = FIRE.enemyAccuracy;
  // ...and the BRACE, on exactly the same gate and for exactly the same reason.
  // A ranged NPC now stops for a beat to shoot instead of firing on the walk,
  // which is what makes a firefight readable at 52 degrees: the shooters are the
  // ones standing still. It is FACTION-MODE ONLY, and that is not taste — with
  // `stance` left null `EnemyManager._stepFire` is bit-identical to the shipped
  // path (its own ctor doc says so), so hero mode's golden checksum in
  // `tests/hero.test.mjs` cannot move BY CONSTRUCTION rather than by argument.
  // The property is live-settable and nothing caches it, so setting it here —
  // after construction, beside its sibling — is the whole wiring.
  if (fac) manager.stance = true;

  const blobs = new ShadowBlobPool({ max: cap + 8, opacity: 0.46, nightFade: 0.6, registry: reg });
  parent.add(blobs.object);
  /** Blob slot per enemy slot; -1 = none. */
  const blobOf = new Int32Array(cap).fill(-1);
  manager.onSpawn = (i) => {
    blobOf[i] = blobs.acquire();
  };
  manager.onDespawn = (i) => {
    if (blobOf[i] >= 0) blobs.release(blobOf[i]);
    blobOf[i] = -1;
    // The director's bookkeeping (a car seat, a pavement errand) must go with
    // the slot, or a recycled one inherits somebody else's job. The mission
    // director is on the same door and for the same reason — plus one of its
    // own: a hostile whose slot freed without ever being seen DYING still has
    // to count toward an `eliminate`.
    population?.onSlotFreed(i);
    missions?.onSlotFreed(i);
    // ...and the line the slot was halfway through saying. `EnemyManager`
    // recycles a freed slot LIFO INSIDE THE SAME STEP, so without this a fresh
    // pedestrian inherits the previous occupant's half-typed sentence and
    // finishes it in the street.
    speech?.onSlotFreed(i);
  };
  const playerBlob = blobs.acquire();

  const spriteRenderer = new SpriteEnemyRenderer({ types, caps: engine.caps, max: cap, parent, name: 'enemySprites' });
  const rigRenderer = new RigEnemyRenderer({ types, max: cap, parent, probes, name: 'enemyRigs' });

  // ── per-type renderer routing (decided ONCE — see the header) ──────────────
  // A creature is either 2D art or a 3D rig. A type carrying a rig draws
  // through the rig renderer; everything else through the sprite renderer.
  // A type carrying NEITHER draws nothing at all, which is why `loadEnemyType`
  // rejects art-less defs rather than letting an invisible enemy hit you.
  const spriteMask = new Uint8Array(types.length);
  const rigMask = new Uint8Array(types.length);
  for (let i = 0; i < types.length; i++) {
    const useRig = !!types[i]?.rig?.parts?.length;
    rigMask[i] = useRig ? 1 : 0;
    spriteMask[i] = !useRig && types[i]?.sprite?.atlas ? 1 : 0;
  }
  spriteRenderer.setTypeMask(spriteMask);
  rigRenderer.setTypeMask(rigMask);

  // ── the actor depth bias (`render/Billboards.js`) ──────────────────────────
  // A screen-aligned billboard carries ONE depth — its FEET's — so at 52
  // degrees the wall behind an actor beats their head in the depth test and
  // saws them in half. Shift the DRAWN sprite along the view axis toward the
  // camera by `h * sin(pitch)`; on-screen it does not move, in depth it clears
  // whatever is at its back. Both angles come off the rig — never a second
  // copy of 52 — and only the ACTOR renderers opt in (an impostor batch is
  // baked AT this pitch and must not move).
  const depthBias = { x: 0, y: 0, z: 0 };
  const syncDepthBias = () => {
    cameraDepthBias(rig.yaw, rig.pitch, ACTOR_DEPTH_BIAS, depthBias);
    player.setDepthBias(depthBias.x, depthBias.y, depthBias.z);
    spriteRenderer.setDepthBias(depthBias.x, depthBias.y, depthBias.z);
    bubbles?.setDepthBias(depthBias.x, depthBias.y, depthBias.z);
  };

  syncDepthBias();
  spriteRenderer.sync(manager, { camYaw: rig.yaw });
  rigRenderer.sync(manager, { camYaw: rig.yaw, dt: 0, player, targeting: 'player' });

  // ── traffic + driving (only where the level publishes a road network) ──────
  /** @type {Traffic|null} */
  let traffic = null;
  /** @type {ReturnType<typeof createDriving>|null} */
  let driving = null;
  /**
   * The fleet is HOISTED out of the `Traffic` argument list because the camera
   * has to reach it every frame and `Traffic` may not import three.js — see
   * `CarFleet#setViewProjection`.
   * @type {CarFleet|null}
   */
  let carFleet = null;
  /**
   * VEHICLE DAMAGE, and it is gated on the SAME data as everything else in this
   * block: no roads, no cars, no damage system, no smoke batch, nothing built
   * and nothing paid. Hero mode reaches none of it — which is also what keeps
   * its golden checksum out of reach of anything below.
   * @type {ReturnType<typeof createVehicleDamage>|null}
   */
  let vehicles = null;
  /** @type {VehicleFxBatch|null} Its smoke/fire/debris. Owned here, like the fleet. */
  let vehicleFx = null;
  if (wantTraffic && level?.map?.roads) {
    const graph = buildLaneGraph(level.map);
    if (graph.laneEdges.length) {
      // The fleet is built HERE and owned by the director: `Traffic` is
      // deliberately three.js-free (so the whole sim is provable in node), so
      // the one piece that needs a GPU is injected. The registry tracks it too,
      // which makes `Level.dispose()` a second, idempotent safety net.
      carFleet = new CarFleet({ parent, registry: reg, maxPerArchetype: 12, name: 'traffic' });
      traffic = new Traffic({
        graph,
        map: level.map,
        fleet: carFleet,
        collision,
        heightAt,
        // The night uniform lives on the level's clock, not ours — read it live
        // so a scrub of the time-of-day slider turns the headlights on now.
        getNight: () => engine.dayNight?.night ?? 0,
        seed,
      });
      traffic.setFocus(player.position.x, player.position.z, rig.yaw);
      traffic.placeParkedCars();
      driving = createDriving({
        traffic,
        player,
        rig,
        input,
        collision,
        heightAt,
        bus,
        bounds: level?.bounds ?? null,
        getNight: () => engine.dayNight?.night ?? 0,
        // THE IMPACT SEAM. `Driving` measures the hit (it is the only place the
        // player's car meets anything) and posts it; the damage system decides
        // what it costs. The closure is what lets the two be built in this
        // order — `vehicles` needs the driving system, and the driving system
        // needs somewhere to report — and the `?.` means a level that somehow
        // has roads but no damage system still drives exactly as before.
        // ...and the CRASH is heard on the same seam, because this is the one
        // place in the game that measures one. `GameAudio` re-derives the gate
        // and the loudness from `VEHICLE`'s own thresholds, so a scrape that
        // costs no health makes no noise; nothing here decides either.
        onImpact: (kind, dv, handle, other) => {
          audio?.carImpact(kind, dv);
          return vehicles?.onImpact(kind, dv, handle, other);
        },
      });

      // ── the damage, and the smoke that shows it ────────────────────────────
      // Built LAST of the vehicle stack because it reads all of it. It imports
      // no three.js of its own — the fx batch is injected exactly the way the
      // fleet is injected into `Traffic`, for exactly the same reason.
      vehicleFx = new VehicleFxBatch({ parent, registry: reg, name: 'vehicleFx' });
      vehicles = createVehicleDamage({
        traffic,
        driving,
        fleet: carFleet,
        fx: vehicleFx,
        manager,
        bus,
        factions: fac,
        heightAt,
        // ⚠ THE PLAYER DOES NOT BLEED IN A CAR, so this is the only door the
        // damage system has to their health, and it is the door an EXPLOSION
        // comes through — see the `'player:damage'` handler below for the other
        // half of that rule. It goes through `player.hurt` + `emitHp` rather
        // than the bus, because re-emitting `'player:damage'` from inside a
        // system that consumes it is a loop waiting to happen.
        //
        // ⚠ THE SECOND ARGUMENT IS ABOUT ARMOUR, NOT ABOUT MAGNITUDE, and the
        // two calls differ in KIND. `Player#hurt` spends the vest before it
        // touches health, so a gunsmith's $120 armour (100) on top of 100 hp is
        // 200 effective: the 140 of a point-blank blast came off as 100
        // absorbed + 40 taken, and the player stepped out of the fireball they
        // were sitting in at 60 hp. That is against the one rule this feature
        // has — you die in a car that explodes under you.
        //   • `lethal` FALSE is blast RADIUS damage, and the vest absorbing it
        //     is exactly what the vest is for. Unchanged behaviour.
        //   • `lethal` TRUE is "you were in it": strip the armour, then take
        //     the health, so no value of `maxArmour` can ever survive it.
        // Do NOT collapse this back to one argument by passing a bigger number
        // — that is the bug it replaced, and the next rise in `maxArmour`
        // defeats any constant the damage table could pick.
        hurtPlayer: (n, lethal = false) => {
          if (player.dead || !(n > 0)) return;
          if (lethal) {
            player.clearArmour?.();
            player.hurt(player.maxHp);
          } else {
            player.hurt(n);
          }
          emitHp();
        },
        // Live reads, never captured values: `player` is hot-swapped when the
        // chibi sprite decodes (see the swap block above) and `driving` owns a
        // phase that changes every time F is pressed.
        playerPos: () => player.position,
        playerInCar: () => !!driving?.inCar,
        seed,
      });
    }
  }

  // ── breakable street furniture (needs cars, and nothing else) ──────────────
  // OPT-IN BY DATA, like traffic: a level that publishes `breakables` gets
  // knock-down lamps and benches that come apart, one that does not gets
  // nothing built. It is gated on `driving` too — a car is the only thing that
  // can break anything (see `Breakables.js`), so without one the system would
  // simulate an event that can never happen.
  /** @type {ReturnType<typeof createBreakables>|null} */
  const breakables = driving && /** @type {any} */ (level)?.breakables?.props?.length
    ? createBreakables({
      .../** @type {any} */ (level).breakables,
      heightAt,
      bus,
      seed,
      // The car pays for what it flattens — and it is THE ONE THAT HIT IT, not
      // whichever one the player happens to be sitting in: they can be driving
      // a second car while the one they bailed out of is still coasting through
      // a bus stop. The velocity is scaled rather than the signed speed,
      // because `stepCarBody` re-derives `speed` from the velocity vector on
      // its next step — writing `speed` here would be overwritten, and writing
      // both would take the hit twice.
      onSmash: (p, by) => {
        if (!by) return;
        by.vx *= 1 - BREAK.carDrag;
        by.vz *= 1 - BREAK.carDrag;
      },
    })
    : null;
  /** The bodies the breakables hit test sees. Reused — this is a per-step read. */
  const breakCars = [];

  // ── population (AFTER traffic: it seats people in the cars) ────────────────
  if (fac) {
    population = createPopulation({
      manager,
      types,
      map: level.map,
      factions: fac,
      traffic,
      collision,
      heightAt,
      bounds: level?.bounds ?? null,
      seed,
    });
  }

  // ── the wanted level (AFTER the population: it drives its seams) ───────────
  // It subscribes to `'crime'` in its constructor, so it is live from here; the
  // response half only exists where there are pavements AND roads to put police
  // on. Without a faction table there is no crime channel and none of this is
  // built — a dev scene pays nothing.
  /** @type {ReturnType<typeof createWanted>|null} */
  const wanted = fac
    ? createWanted({
      bus,
      factions: fac,
      manager,
      types,
      population,
      traffic,
      collision,
      heightAt,
      bounds: level?.bounds ?? null,
      seed,
    })
    : null;

  // ── the vehicle sequence (AFTER the population: it borrows its occupants) ──
  // One machine for every way in and out of a car — see `systems/Carjack.js`.
  // Without a population it is simply the door choreography on an empty car,
  // which is exactly what a level with roads but no factions should get.
  /** @type {ReturnType<typeof createCarjack>|null} */
  const carjack = traffic && driving
    ? createCarjack({
      traffic,
      driving,
      player,
      manager,
      population,
      factions: fac,
      bus,
      collision,
      heightAt,
    })
    : null;

  // ── the money (ALWAYS, and it is one number) ───────────────────────────────
  // Unconditional where everything around it is opt-in, because the balance is
  // the player's, not a subsystem's: a level with no shops and no missions still
  // has a wallet reading `$500`, and `ui/hud/CashBar.jsx` renders from the emit
  // this construction makes. Missions pay into it and the till spends out of it;
  // nobody else keeps a copy of the figure.
  const wallet = createWallet({ bus });

  // ── the legs (ALWAYS, and for the wallet's exact reason) ───────────────────
  // Unconditional where `weapons`, `factions` and `missions` are all
  // content-gated, because the sprint is THE PLAYER'S — the same sentence the
  // wallet's note above makes about the balance. Three things follow from it and
  // each one is a reason on its own:
  //  • there is no table to inject. A wallet is a number and a stamina bar is a
  //    number; neither has content behind it to be missing.
  //  • it draws NO DICE, so hero mode's golden checksum (1878938342) is safe BY
  //    CONSTRUCTION rather than by a gate — the same standing this has as
  //    `Player#speedMul` being inert at 1.
  //  • gating it would leave every dev scene with a player who cannot run, and
  //    a dev scene is precisely where a movement feel gets judged.
  //
  // The bus is handed over for the reason `Wallet.js` and `Weapons.js` take
  // one: whatever it has to say about the bar it says ITSELF, synchronously,
  // and nothing in this file ever publishes on its behalf.
  const stamina = createStamina({ bus });

  // ── the gun's magazine (CONTENT-GATED, like factions and missions) ─────────
  // Built beside the wallet because it is the same kind of thing — a small
  // number the player spends and refills, owned in one place and published by
  // itself — and BEFORE the shop, which buys rounds into it through the
  // `loadout` seam a few blocks down.
  //
  // ⚠ NULL WITHOUT A TABLE, and that null is load-bearing rather than defensive:
  // every dev scene and every suite constructs gameplay without one, and with
  // `weapons` null the auto-attack is byte-for-byte the cadence that shipped.
  // `Weapons.js` itself tolerates `table: null`, but building one anyway would
  // put an inert `'player:ammo'` emit and a permanently-false `canFire()` into
  // levels that never asked for a gun with a clip.
  //
  // ⚠ IT PUBLISHES ITS OWN CHANNEL, SYNCHRONOUSLY, AND IT IS THE ONLY THING
  // THAT MAY — see the BUS CONTRACT note in the header.
  const weapons = weaponTable ? createWeapons({ table: weaponTable, bus }) : null;

  /**
   * @type {ReturnType<typeof createShops>|null} THE SHOP — built two blocks
   * below, after the door system it reads. Declared HERE so the door system's
   * `extraPrompt` can close over it; that closure is only ever called from the
   * ~4 Hz prompt scan, by which time this is assigned.
   */
  let shops = null;

  // ── the door sequence (AFTER the carjack: it defers to it) ─────────────────
  // OPT-IN BY DATA, exactly like traffic and the breakables: a level that
  // publishes `doors` + `interiors` gets storefronts you can walk into, one that
  // does not (every dev scene, every test) builds nothing and pays nothing. It
  // is constructed after `carjack` because it asks that machine whether a
  // sequence is running before it claims F — see `systems/Interiors.js#busy`.
  //
  // ⚠ `canEnter` is the v1 gate and it is deliberately blunt: NO DOOR OPENS
  // WHILE A MISSION IS RUNNING. An escort has no pathfinding and no way under
  // the city, so a player who ducks into a sandwich shop mid-job strands their
  // crew on the pavement with a home they cannot reach. `missions.active` is the
  // ACTIVE (or briefing) mission's id or `''`, and it is absent entirely on a
  // level with no script — both read as "go ahead".
  /** @type {ReturnType<typeof createInteriors>|null} */
  const interiors = /** @type {any} */ (level)?.doors?.length && /** @type {any} */ (level)?.interiors
    ? createInteriors({
      player,
      rig,
      bus,
      level,
      driving,
      carjack,
      canEnter: () => !missions?.active,
      // THE INDOOR PROMPT, borrowed. The till has something to say while the
      // player is in a room and off the exit mat, and it says it through this
      // system's publisher rather than opening a second one on the
      // edge-detected `'hud:prompt'` channel — see `Interiors.js`'s header.
      extraPrompt: () => shops?.prompt ?? null,
      // The OUTDOOR sampler. The room's own floor is flat and the system swaps
      // to its own closure at the SWAP; this one is what stands the player back
      // on the pavement when they come out.
      heightAt,
    })
    : null;

  // ── the shop (AFTER the door: it is a lifecycle hung off `interiors.plan`) ──
  // Gated on the DOOR SYSTEM, not on the level data, because a room is the only
  // thing this system has an opinion about: no doors, no rooms, no crowd, no
  // till. It is built even without a faction table — `civTypes` is then empty,
  // nobody is spawned, and the till still serves (a shop with no modelled keeper
  // is a shop whose keeper was never shot).
  //
  // The civilian type indices come from the SAME `factionForId` routing
  // `systems/Population.js` uses, so "who can be a shopkeeper" and "who can be a
  // pedestrian" can never drift apart into two lists somebody has to maintain.
  /** @type {number[]} */
  const civTypes = [];
  if (fac?.factionForId) {
    for (let i = 0; i < types.length; i++) {
      if (fac.factionForId(types[i]?.id) === fac.CIVILIAN) civTypes.push(i);
    }
  }
  if (interiors) {
    shops = createShops({
      manager,
      interiors,
      wallet,
      player,
      bus,
      civTypes,
      seed,
      factions: fac,
      // THE RESERVED SEAM, SPENT. `Shops.js` has carried a null `loadout` since
      // it shipped, documented as "the ownership seam"; the `ammo` verb is the
      // first thing to reach through it, and it reaches the weapons model and
      // nothing else. Null here (no table) makes that verb's `available` false
      // and DROPS the row — the game cannot deliver it — which is exactly the
      // available/can split `docs/shops.md` insists on.
      loadout: weapons,
    });
  }

  // ── missions (LAST: it borrows everything above) ───────────────────────────
  // Content-gated exactly like the faction table, and gated ON it: every
  // mission actor needs an allegiance. The marker batch is only built when
  // there is a director to feed it, so a level with no missions pays nothing —
  // not a draw call, not a texture.
  /** @type {MissionMarkerBatch|null} */
  let markerBatch = null;
  if (fac && missionScript?.missions?.length) {
    missions = createMissions({
      manager,
      types,
      script: missionScript,
      factions: fac,
      player,
      map: level?.map ?? null,
      population,
      traffic,
      driving,
      bus,
      collision,
      heightAt,
      bounds: level?.bounds ?? null,
      spawn: { x: spawn.x, z: spawn.z },
      seed,
      // THE PAYOUT. A mission's `reward` is paid on a PASS and never on a fail;
      // the director owns the "exactly once" (it pays inside `complete`, which
      // a mission reaches once per run), the wallet owns the clamp.
      wallet,
      // THE TELEPORT. See the header: a driving player is moved car and all,
      // because the alternative is a car abandoned on the far side of the
      // island the instant a cutscene ends.
      teleport: (x, z) => {
        const y = heightAt(x, z);
        if (driving?.inCar) {
          const b = driving.body;
          b.x = x;
          b.y = y;
          b.z = z;
          b.vx = 0;
          b.vz = 0;
          b.speed = 0;
          // `load` and `steerAngle` are STATE (see CLAUDE.md's driving note) —
          // a car that arrives still leaning on a corner it left behind pitches
          // for no reason.
          b.load = 0;
          b.steerAngle = 0;
        }
        player.moveTo(x, z, y);
        rig.snapTo(player.position);
      },
    });
    markerBatch = new MissionMarkerBatch({ registry: reg, name: 'missionMarkers' });
    parent.add(markerBatch.object);
  }

  // ── door rings (the same batch class, a second instance) ───────────────────
  // A door is invisible from a 52-degree camera: the storefront wall is one more
  // baked facade, so without a mark on the ground there is nothing to tell the
  // player that THIS shop is a place and the twelve beside it are scenery. The
  // ring is the mission marker's ring on purpose — one shape means one rule
  // ("stand in the circle") — and it is COOL WHITE at low alpha where a mission
  // is gold, so the two never read as the same offer.
  /** @type {MissionMarkerBatch|null} */
  let doorMarkers = null;
  /** @type {any[]} The level's outdoor door records; empty without interiors. */
  const levelDoors = interiors ? /** @type {any} */ (level).doors : [];
  if (interiors) {
    doorMarkers = new MissionMarkerBatch({ registry: reg, max: DOORS.max, name: 'doorMarkers' });
    parent.add(doorMarkers.object);
  }
  /** Nearest-first scratch for the ring set. Allocated once, like the blips'. */
  const doorD2 = new Float32Array(DOORS.max);
  const doorIdx = new Int32Array(DOORS.max);

  // ── speech bubbles (OPT-IN BY CONTENT, exactly like factions and missions) ──
  // The lines are game CONTENT — who says what, in whose voice — so the ROUTE
  // builds the bank and hands it down, and the engine goes on never importing
  // `src/game/`. Pass nothing and not one branch of this runs: no director, no
  // batch, no draw call, and the step below is a dead `if`.
  //
  // It is built HERE, at the end of the construction, for one reason: it reads
  // `wanted?.level` (a city under three stars does not chat), so it cannot be
  // built before the wanted system exists. `parent` and `reg` are the batch's
  // other two dependencies and both have been settled since the top.
  //
  // ⚠ IT PUBLISHES NOTHING AND SUBSCRIBES TO NOTHING — do not go looking for
  // the channel. A bubble is a pure read of manager state plus a clock, drawn
  // from a per-frame rebuild, so there is no event for the HUD to listen on and
  // nothing here to add to the bus table in the header.
  if (dialogueScript) {
    bubbles = new SpeechBubbleBatch({
      cells: SPEECH.maxLive,
      caps: engine.caps,
      parent,
      registry: reg,
      name: 'speechBubbles',
    });
    speech = createSpeech({
      manager,
      bus,
      script: dialogueScript,
      factions: fac,
      wanted,
      seed,
    });
  }

  // ── the resupply drone (OPT-IN ON THE WEAPON TABLE) ────────────────────────
  // When the reserve drops below one clip a drone flies in and credits rounds,
  // free, with no key and no prompt: this game is going to a touchscreen and the
  // whole combat design is auto-combat, so a resupply the player has to ASK for
  // is a resupply that arrives during a firefight as a second thing to do. The
  // model is `systems/Drone.js` (no three.js, node-testable, SOLE publisher of
  // `'drone:state'`/`'drone:delivered'` and SOLE subscriber to `'player:ammo'`),
  // the pixels are `render/DroneMesh.js`; both files carry their own traps in
  // their headers and this block deliberately does not restate them.
  //
  // ⚠ THE GATE IS `weapons` — THE INJECTED WEAPON TABLE — and it is the same
  // opt-in-by-content discipline as `factions`, `missions`, `dialogue` and the
  // magazine itself, not a defensive null check. Two things fall out of it, and
  // both matter:
  //
  //  • It is SEMANTIC first. A drone with no gun to feed has nothing to deliver
  //    and no threshold to cross — `Drone.js`'s `payload`/`lowAt` both default
  //    off `weapons`, so a drone built without one is inert by construction
  //    anyway. Building the mesh regardless would be SIX draws (five meshes
  //    plus the ring's own batch — `DroneMesh#stats().draws` is the number) and
  //    a baked geometry bought for a machine that can never fly.
  //  • Every dev scene and every node suite constructs gameplay WITHOUT a
  //    weapon table, so `drone` is null there, not one branch below runs, and
  //    `tests/hero.test.mjs`'s golden checksum 1878938342 is safe BY
  //    CONSTRUCTION rather than by argument — the same reasoning that keeps
  //    `manager.accuracy`/`manager.stance` behind `if (fac)`.
  //
  // It is built HERE, beside the speech bubbles and for the same two reasons:
  // it reads `interiors` (settled well above — a player at a shop counter is
  // 100 m under the island and a delivery must DEFER, not fly down through the
  // rock), and like the talking it is one of the last things assembled, so
  // everything it borrows has already been decided.
  /** @type {ReturnType<typeof createDrone>|null} THE MODEL. Null without a gun. */
  let drone = weapons ? createDrone({ bus, weapons, player, interiors, heightAt, seed }) : null;
  /** @type {DroneMesh|null} Its pixels — airframe, four rotors and a ground ring. */
  let droneMesh = drone ? new DroneMesh({ registry: reg, parent, heightAt, name: 'drone' }) : null;
  // Hidden from the first frame: the drone is IDLE and NOWHERE for most of a
  // session, and `setPose` has never been called, so an unhidden mesh would sit
  // at the world origin with its ring on the tarmac until the first sortie.
  droneMesh?.setVisible(false);

  // ── the phone ──────────────────────────────────────────────────────────────
  //
  // Built LAST of the gameplay systems, because it borrows from three of them
  // and owns none: the DRONE (its only outward call is `forceOrder()`), the GUN
  // (`ammoPerBox` for the row's sentence, `roomForAmmo()` for its one refusal)
  // and `interiors`/`player` for the two states in which a panel must not open.
  //
  // ⚠ IT HAS NO `update()` AND NO STEP SLOT, and that is the `Wallet.js`
  // precedent rather than an omission — money does not tick and neither does a
  // phone. Both of its overlays FREEZE the loop, so there is no state that can
  // change underneath a live call except a teardown, which `dispose()` covers.
  // Do not add a poll here; the ring arrives on `'mission:offer'`, which
  // `Missions.js` drains on a step.
  //
  // ⚠ THE GATE IS TWO CONTENT SEAMS, EITHER OF THEM. A mission script gives it
  // calls; a weapon table gives it a row. With neither there is nothing a phone
  // could show, so hero mode — which injects neither — builds none at all, and
  // the golden checksum 1878938342 is safe BY CONSTRUCTION. Belt and braces: the
  // file draws no rng from any stream, so a phone that WAS built could not move
  // a draw sequence either.
  /** @type {ReturnType<typeof createPhone>|null} Null without content. */
  let phone = (missions || weapons)
    ? createPhone({ bus, weapons, drone, player, interiors })
    : null;

  // ── the radio (CONTENT-GATED, the sixth one) ───────────────────────────────
  //
  // A station is ALWAYS ON AIR. `systems/Radio.js` is a pure model — no Web
  // Audio, no three.js, no clock of its own — that knows which track is playing
  // and how far into it the world is; `systems/GameAudio.js` is the only thing
  // that talks to it, and `render` is where it is told the time. Nothing in
  // THIS file steps it, reads it or branches on it.
  //
  // ⚠ THE GATE IS `radioTable`, the same opt-in-by-content discipline as the
  // five seams above. With no table nothing is constructed, its private
  // generator is never seeded and never drawn from, and `'radio:track'` is a
  // channel with no publisher — so hero mode's checksum 1878938342 is safe BY
  // CONSTRUCTION rather than by argument, the `factions` idiom again.
  //
  // ⚠ IT IS BUILT BEFORE THE SOUND AND THAT ORDER IS REAL: `createGameAudio`
  // takes it as an argument. A radio built without a sound table is legal and
  // INERT — nobody calls `update()`, so the clock never advances and no track
  // ever starts — which is the honest behaviour: a HUD naming a station over a
  // silent level would be a lie the model told on the wiring's behalf.
  /** @type {ReturnType<typeof createRadio>|null} THE STATION. Null without content. */
  let radio = radioTable ? createRadio({ table: radioTable, bus, seed }) : null;

  // ── the sound (CONTENT-GATED, the fifth one, and the last thing built) ─────
  //
  // `engine/audio/Sfx.js` is the machine (mixer + Web Audio) and
  // `systems/GameAudio.js` is the WIRING — what plays when. Both are built from
  // ONE injected table, and the split is the `systems/Speech.js` /
  // `render/SpeechBubbles.js` shape: a node-testable decider that imports no
  // Web Audio, joined here to the half that owns the hardware.
  //
  // ⚠ THE GATE IS `soundTable`, and it is the same opt-in-by-content discipline
  // as `factions`, `missions`, `dialogue` and `weapons` — not a defensive null
  // check. With no table nothing is constructed, no `AudioContext` is created,
  // not one bus subscription is opened and every call site below is `audio?.…`
  // against a null. Every dev scene and every node suite builds gameplay
  // without one, so hero mode's golden checksum 1878938342 is safe BY
  // CONSTRUCTION rather than by argument. Belt and braces: `GameAudio` draws no
  // rng from any stream at all, so a system that WAS built could not move a
  // draw sequence either.
  //
  // It is built LAST because it reads more of the assembly than anything else
  // does — the driving body, the traffic pool, the wanted level, the manager,
  // the drone and the door system are all settled by here — and it is the ONE
  // system that must also survive a frozen loop, which is why it is the only
  // one in this file with a `render` hook as well as an `update`. See
  // `GameAudio.js`'s header for that split; it is the whole design.
  /** @type {ReturnType<typeof createSfx>|null} THE MACHINE. Null without content. */
  let sfx = soundTable ? createSfx({ table: soundTable }) : null;
  /** @type {ReturnType<typeof createGameAudio>|null} THE WIRING. */
  let audio = sfx
    ? createGameAudio({
      sfx,
      bus,
      player,
      rig,
      driving,
      traffic,
      // ⚠ ONE INJECTION, TWO READINGS, AND NEITHER OF THEM IS THE MANAGER.
      // `wanted` carries both halves of the siren now — the LEVEL (is one
      // running) off the bus, and `response.nearestSiren` (WHERE it stands,
      // which is the nearest police CAR whose lightbar is already flashing).
      // `manager` and `factions` used to be passed for exactly one thing, a
      // walk for the nearest police PERSON, and ★1 is officers-only, so the
      // siren came out of a man on the pavement. See `GameAudio.js#siren`.
      wanted,
      drone,
      radio,
      radioTune,
      interiors,
      heightAt,
    })
    : null;

  // ── shot feedback ──────────────────────────────────────────────────────────
  const tracers = createTracerPool(FIRE.tracers, parent, {
    life: FIRE.tracerLife,
    width: FIRE.tracerWidth,
    speed: FIRE.tracerSpeed,
    trail: FIRE.tracerTrail,
  });
  const blood = createBloodPool(parent, { heightAt, rng: `${seed}:blood` });

  // ── faction feedback: enemy shots, crimes, and the noise they make ─────────
  if (fac) {
    // Provocation + the `'crime'` taxonomy live in `Population.js` (which has
    // no three.js, so they are provable in node); what stays here is the half
    // that needs a GPU.
    attachFactionCombat({ manager, bus, factions: fac, population });

    // Ranged units resolve their own shots inside the manager; this is the
    // tracer/blood/noise hook, played after the fact. `target < 0` means the
    // round was aimed at the PLAYER, which is not a slot.
    manager.onFire = (i, tgt, m, shot) => {
      const shooter = m.typeOf(i);
      const muzzleY = m.y[i] + (shooter?.worldHeight ?? 1.3) * 0.55;
      let impactY;
      if (!shot.hit) impactY = heightAt(shot.x, shot.z) + FIRE.missY;
      else if (tgt >= 0) impactY = m.y[tgt] + (m.typeOf(tgt)?.worldHeight ?? 1.3) * 0.5;
      else impactY = player.position.y + FIRE.tracerY;
      tracers.fire(m.x[i], muzzleY, m.z[i], shot.x, impactY, shot.z);
      if (shot.hit && tgt >= 0) {
        blood.splash(m.x[tgt], impactY, m.z[tgt], m.x[tgt] - m.x[i], m.z[tgt] - m.z[i]);
      }
      audio?.npcShot(m.x[i], muzzleY, m.z[i]);
      population?.alarm(m.x[i], m.z[i], PED_TUNING.shotRadius);
      population?.alarm(shot.x, shot.z, PED_TUNING.shotRadius);
    };
  }

  // ── the groan (CHAINED onto the faction combat hooks) ──────────────────────
  // ⚠ `manager.onDamage` and `manager.onKill` are ALREADY OWNED — by
  // `attachFactionCombat` a few lines up, which is where the crime taxonomy
  // lives. So this is a LINK in a chain, never a replacement for one: capture
  // whatever is installed, call it, then ours. `Shops.js` does exactly this to
  // `manager.onDespawn` and its header records the rule.
  //
  // ⚠ THE PREVIOUS HANDLER RUNS FIRST, deliberately. It is the one that
  // provokes a faction and bills a crime, and a sound is never allowed to sit
  // in front of a consequence.
  //
  // Both are restored in `dispose()` the same way — see the teardown, which
  // nulls the five `manager.on*` callbacks and therefore must not run before
  // this link has been taken out of the chain.
  //
  // ⚠ THE WHOLE BLOCK IS GATED ON `audio`, like every other content-gated
  // system in this file: with no sound table the two callbacks are exactly what
  // `attachFactionCombat` left them, so hero mode's manager path is untouched.
  /** @type {(() => void)|null} Unhooks the two links. Set only when we hooked. */
  let unchainCombat = null;
  if (audio) {
    const prevOnDamage = typeof manager.onDamage === 'function' ? manager.onDamage : null;
    const prevOnKill = typeof manager.onKill === 'function' ? manager.onKill : null;
    /** Chest height off the victim's FEET — `manager.y` is the ground under it,
     *  the same reading `onFire`'s muzzle and `fire()`'s blood both take. */
    const mouth = (i) => manager.y[i] + (manager.typeOf(i)?.worldHeight ?? 1.3) * 0.55;
    /** @param {number} i @param {number} src @param {number} amt @param {any} m */
    const audioOnDamage = (i, src, amt, m) => {
      prevOnDamage?.(i, src, amt, m);
      audio?.groanAt(manager.x[i], mouth(i), manager.z[i]);
    };
    /** @param {number} i @param {any} m */
    const audioOnKill = (i, m) => {
      prevOnKill?.(i, m);
      audio?.groanAt(manager.x[i], mouth(i), manager.z[i]);
    };
    manager.onDamage = audioOnDamage;
    manager.onKill = audioOnKill;
    // ⚠ ONLY IF WE ARE STILL THE LINK THAT IS WIRED IN — `Shops#dispose`'s rule.
    // Somebody who reassigned the callback after us owns it now, and restoring
    // our captured predecessor over the top of theirs would silently delete a
    // director's bookkeeping.
    unchainCombat = () => {
      if (manager.onDamage === audioOnDamage) manager.onDamage = prevOnDamage;
      if (manager.onKill === audioOnKill) manager.onKill = prevOnKill;
    };
  }

  // ── bus ────────────────────────────────────────────────────────────────────
  let kills = 0;
  let lastHp = -1;
  /**
   * ...and the vest, edge-detected beside it. Armour rides the SAME channel as
   * health because it is the same reading — `ui/hud/HealthBar.jsx` draws the
   * strip over the segments — and because a second channel would need a second
   * change gate that could disagree with this one. Buying armour moves nothing
   * about hp, so without this the purchase would not repaint the HUD until the
   * next time the player was shot.
   */
  let lastArmour = -1;
  const emitHp = () => {
    if (player.hp === lastHp && player.armour === lastArmour) return;
    lastHp = player.hp;
    lastArmour = player.armour;
    bus.emit('player:hp', {
      hp: player.hp,
      max: player.maxHp,
      armour: player.armour,
      maxArmour: player.maxArmour,
    });
  };

  const offs = [
    /**
     * ⚠ THE PLAYER DOES NOT BLEED IN A CAR (owner requirement). Every round
     * that would have hit them while they are driving hits the CAR instead,
     * scaled by {@link VEHICLE.bulletToCar}, and their own health is not
     * touched at all.
     *
     * THE CONSEQUENCE IS THE POINT, not a side effect: a player who sits in a
     * car under police fire is now killed by the CAR going up rather than by
     * the bullets. That is the genre's rule, it is what makes the smoke and the
     * fire mean something (a health bar that never moves while the bodywork
     * blackens is the whole read), and it is why parking in a firefight is a
     * decision instead of a hiding place. The explosion's damage comes back to
     * the player through the damage system's own `hurtPlayer`, so death in a
     * car is still death — it just arrives on the car's clock.
     *
     * ON FOOT this is byte-identical to what it always was, which is the test
     * to keep: hero mode, every dev scene and every level without roads reach
     * `vehicles === null` and fall straight through.
     */
    bus.on('player:damage', (p) => {
      const amount = typeof p === 'number' ? p : p?.amount ?? 0;
      if (!(amount > 0) || player.dead) return;
      if (vehicles && driving?.inCar && driving.fleetHandle >= 0) {
        // WHICH SIDE WAS SHOT. `p.from` is the shooter's manager slot when
        // there is one (`EnemyManager` puts it on every emit); the vector is
        // shooter → car, i.e. the direction the ROUND travelled, which is the
        // same convention `EnemyManager#damage` and `fire()` use for knockback.
        // A source with no slot (a scripted hit, the dev scene's H key) leaves
        // it (0, 0) and the damage system picks a panel however it likes.
        const from = typeof p === 'object' && p ? p.from ?? -1 : -1;
        const shot = from >= 0 && from < manager.top;
        vehicles.damage(driving.fleetHandle, amount * VEHICLE.bulletToCar, {
          kx: shot ? driving.body.x - manager.x[from] : 0,
          kz: shot ? driving.body.z - manager.z[from] : 0,
          byPlayer: false,
        });
        return;
      }
      player.hurt(amount);
      // ⚠ HERE, NOT AT THE TOP OF THIS HANDLER. The branch above redirects the
      // round into the CAR and returns — the player does not bleed in a car —
      // so a groan wired to the channel would have them crying out at every
      // bullet that hit the bodywork.
      audio?.playerHurt();
      emitHp();
    }),
    // Q/E rotate FREELY while held (see the per-step `spinYaw` below). Shift+Q/E
    // keeps the old exact 90-degree snap, which is still the fastest way to get
    // a square framing back and the only way to land on a yaw where tree
    // impostors are exact rather than approximate.
    //
    // ⚠ SHIFT IS ALSO THE RUN KEY (step 0), SO THESE TWO BINDINGS COLLIDE, AND
    // THE COLLISION IS ACCEPTED RATHER THAN OVERLOOKED. A player who holds
    // Shift to run and taps Q or E gets a 90-degree SNAP, not a free spin —
    // both bindings keep exactly the meaning they already had, and neither
    // silently swallows the other's press. Shift is the run key the owner asked
    // for; the snap is the older claim on it and still the only way to land on
    // an exact yaw. The escape hatch is that the OTHER spelling of the same
    // control is untouched: LEFT-CLICK-DRAG (`input/DragYaw.js`) spins the
    // camera freely at any speed, running or not. Moving either binding is an
    // owner decision, not a tidy-up.
    input.onKey('KeyQ', () => {
      if (input.down('ShiftLeft') || input.down('ShiftRight')) rig.rotateYawStep(-1);
    }),
    input.onKey('KeyE', () => {
      if (input.down('ShiftLeft') || input.down('ShiftRight')) rig.rotateYawStep(1);
    }),
    // `baseDistance`, not `distance`: while driving the live distance carries
    // the rig's drive boost, so zooming off it would ratchet the base outward.
    input.onKey('BracketLeft', () => rig.setZoom(rig.baseDistance - 4)),
    input.onKey('BracketRight', () => rig.setZoom(rig.baseDistance + 4)),
    // F is the whole vehicle UI, and it goes through the SEQUENCE rather than
    // straight at `Driving.toggle()`: in beside a parked car, into somebody
    // else's by dragging them out of it, out when stopped. Nothing interrupts
    // one once it starts — see `systems/Carjack.js`.
    //
    // ...and F is also the whole DOOR UI, so `systems/Interiors.js` is offered
    // the press FIRST and the vehicle machine takes it on a false. That is not
    // a priority fight: a door ring is never inside a car, so `press()` only
    // returns true when the player is on foot standing on a mat, and every car
    // interaction reaches `carjack` exactly as it did before. `Interiors` also
    // asks `Carjack` whether a sequence is running before it claims anything,
    // so the two can never both own the same press.
    //
    // ...and F is the TILL as well, offered between the two. The order is
    // explicit rather than incidental: `interiors` still gets the first refusal
    // (a door is the only thing that can move the player out of the world), the
    // till only ever answers inside a room, and a car is never inside a room —
    // so the three can only disagree about a press none of them wants.
    input.onKey('KeyF', () => {
      if (interiors?.press()) return;
      if (shops?.press()) return;
      if (carjack) carjack.press();
      else driving?.toggle();
    }),
    // R RELOADS AND X IS THE SAFETY CATCH — the only two things the player may
    // say to their own gun, and both are no-ops without a weapon table.
    //
    // ⚠ THE MODIFIER GUARD IS MANDATORY, NOT TIDINESS. Ctrl+R is RELOAD THE PAGE
    // and Cmd+R is the same on a Mac; swallowing them would cost the player
    // their browser refresh in exchange for a magazine they did not ask for.
    // Alt is in the list because Alt+R opens a menu on some platforms. There is
    // nothing to preventDefault here — `input/Keyboard.js`'s `DEFAULT_SWALLOW`
    // is for keys whose BROWSER default scrolls the page, and neither of these
    // belongs in it.
    //
    // Neither key contends with anything: the bound set is WASD, the arrows,
    // Q/E (+Shift), `[`/`]`, F, and Space (the handbrake).
    input.onKey('KeyR', (c, e) => {
      if (e?.ctrlKey || e?.metaKey || e?.altKey) return;
      weapons?.reload();
    }),
    // ⚠ X is a TOGGLE and it goes through the model, never through a local flag:
    // the HUD's tap target reaches the same method over `'weapon:hot'`, so a
    // touch player and a keyboard player share one stance and neither path can
    // grow its own rules (the `shops.press()` vs `'shop:buy'` shape).
    input.onKey('KeyX', (c, e) => {
      if (e?.ctrlKey || e?.metaKey || e?.altKey) return;
      weapons?.toggleHot();
    }),
    // P IS THE PHONE, and it is ONE contextual door rather than three keys —
    // `press()` advances a call that is on screen, answers one that is ringing,
    // and otherwise opens the panel. That is `interiors → shops → carjack`'s
    // shape, and the reason is the same: the key and the tap must end in the
    // same method, or the touch path and the keyboard path grow their own rules.
    //
    // ⚠ IT IS DELIBERATELY NOT IN THE `KeyF` CHAIN. F already arbitrates three
    // claimants that can only disagree about a press none of them wants; a
    // phone can be opened anywhere, including at a till and inside a car, so a
    // fourth claimant there is how that chain stops being predictable.
    //
    // ⚠ THE MODIFIER GUARD IS MANDATORY FOR A REASON THIS PROJECT HAS NOT HIT
    // YET: Ctrl+P is PRINT. Swallowing it would cost the player their browser's
    // print dialog in exchange for a menu they did not ask for — the same trade
    // the R/X guards above refuse.
    //
    // Nothing contends: the bound set is WASD, the arrows, Q/E (+Shift),
    // `[`/`]`, F, Space, R and X. While either phone overlay is up its own
    // capture listener swallows P before the sim sees it, so the panel cannot
    // re-open itself on the press that closed it.
    input.onKey('KeyP', (c, e) => {
      if (e?.ctrlKey || e?.metaKey || e?.altKey) return;
      phone?.press();
    }),
  ];

  /**
   * A CAR HAS DIED: retire its slot.
   *
   * `systems/VehicleDamage.js` deliberately touches neither `Traffic` nor
   * `Driving` — it owns hit points, panels and fire, and the moment it started
   * moving slots between pools there would be a second handover path (see
   * `Traffic#takeDriving`). So it announces, and this decides. There are four
   * places a car can be when it dies and they are tried in the order a handle
   * can be in only one of them:
   *
   *  1. THE PLAYER IS IN IT. Force them out (`force` parks it where it stands
   *     and never coasts — a burning car rolling on driverless is a car the
   *     player wakes up chasing) and mark the slot they came out of. The slot
   *     is captured BEFORE the exit because `finishExit` clears `carSlot`, and
   *     it is marked whether or not the exit took: mid-`'entering'` the exit
   *     refuses, and the byte then rides the slot through the entry and out the
   *     other side, because `returnParked` never clears it.
   *  2. IT IS THE ONE THEY BAILED OUT OF. The coast is left to finish — it is
   *     already braking to a stop and stopping it dead mid-street would read as
   *     a car hitting an invisible wall — and the slot it is going to be handed
   *     back into is marked NOW. Same reason as above: `parkCoast` goes through
   *     `returnParked`, which leaves the byte alone.
   *  3. A LIVE AI CAR. `wreckDriving` = `takeDriving` + the byte: the one
   *     handover path, the population seam fired exactly once, the occupants
   *     cleared by `Population`.
   *  4. AN ALREADY-PARKED CAR. One byte; it was furniture already.
   *
   * ⚠ AND IT MUST NOT FIGHT THE RESPAWN. Step 3b calls `driving?.exitCar(true)`
   * three seconds after the player dies, and a player killed by their own car's
   * explosion reaches BOTH paths. They cannot collide, and the reason is that
   * neither one is a state machine: case 1 leaves `phase` at `'foot'` and
   * `carSlot` at −1, so the respawn's `exitCar` returns false and does nothing;
   * and if the respawn happens to get there first (the player was shot dead in
   * the same step the car blew up), case 1's test fails, the walk finds the car
   * as an ordinary parked slot and case 4 marks it. Either order, one wreck,
   * standing exactly where it blew up, and the player wakes up in the plaza.
   * Marking is idempotent, so a repeat announcement costs a byte write.
   *
   * @param {number} handle Fleet handle from the `'car:wrecked'` payload.
   */
  const retireWreck = (handle) => {
    if (!traffic || !(handle >= 0)) return;
    if (driving && driving.fleetHandle === handle) {
      const slot = driving.carSlot;
      driving.exitCar(true);
      if (slot >= 0) traffic.setWrecked(slot, true);
      return;
    }
    if (driving && driving.coastHandle === handle) {
      if (driving.coastSlot >= 0) traffic.setWrecked(driving.coastSlot, true);
      return;
    }
    // A WALK, not a map. The fleet is a few dozen cars and this runs once per
    // wreck; a handle→slot index would be a third thing to keep honest across
    // `takeDriving`, `returnParked`, the despawn ring and the guest eviction,
    // and every one of those is a place it could go stale unnoticed.
    for (let i = 0; i < traffic.top; i++) {
      if (traffic.isDriving(i) && traffic.drivingHandle(i) === handle) {
        traffic.wreckDriving(i);
        return;
      }
    }
    const p = parkedSlotOf(handle);
    if (p >= 0) traffic.setWrecked(p, true);
  };

  /**
   * Fleet handle → PARKED slot, or −1.
   *
   * THE SAME WALK `retireWreck` has always ended with, lifted out because the
   * despawn timer needs it too — a SECOND walk would be the handle→slot map
   * that note refuses, written twice instead of once.
   *
   * ⚠ `handle >= 0` is what makes the loop safe: a freed slot's `pHandle` is
   * −1, so without the guard a −1 payload would match the first empty slot in
   * the pool and the caller would act on somebody else's bay.
   *
   * @param {number} handle
   * @returns {number} Parked slot, or −1 — including when the car is simply
   *   gone, which is a normal answer and never an error.
   */
  const parkedSlotOf = (handle) => {
    if (!traffic || !(handle >= 0)) return -1;
    for (let p = 0; p < traffic.parkedCap; p++) if (traffic.parkedHandle(p) === handle) return p;
    return -1;
  };

  /**
   * A WRECK'S TIME IS UP: take the car out of the world.
   *
   * Owner request — a burnt-out shell used to leave only on DISTANCE (the ring
   * collects a guest slot past `TRAFFIC.despawn`, a budgeted bay past
   * `parkDespawn`), so standing next to one kept it forever. The clock lives in
   * `systems/VehicleDamage.js`, which announces and — like `'car:wrecked'` —
   * touches neither `Traffic` nor `Driving` itself. Removing the car is here.
   *
   * ⚠ "NOT FOUND" IS THE NORMAL CASE, NOT AN ERROR. Sixty seconds is long
   * enough for the player to have driven three districts away and the despawn
   * ring to have collected the wreck already, at which point this event names a
   * fleet handle that no longer belongs to anything — and the handle may by
   * then have been reissued to a brand new car, which is exactly why the walk
   * is by handle over the CURRENT pool and the answer is acted on only when it
   * is a live slot. No log, no throw: a timer firing on a car the world already
   * tidied away is the system working.
   *
   * ⚠ AND IT MAY NEVER FREE A CAR THE PLAYER IS IN. A wreck is not enterable
   * (`Traffic#nearestEnterable` skips it) so this should be unreachable, but
   * the failure mode if it ever is reached — the player standing inside a car
   * that ceases to exist — is the unrecoverable class of bug `Interiors#abort`
   * is built around. So it is refused TWICE, on purpose: `driving`'s two live
   * handles are checked here, and `despawnParked` refuses `CAR_STATE.PLAYER`
   * again on its own. Belt and braces is the right amount of caution for a
   * state with no way back.
   *
   * @param {number} handle Fleet handle from the `'car:despawn'` payload.
   */
  const despawnCar = (handle) => {
    if (!traffic || !(handle >= 0)) return;
    // The player's car, and the one they bailed out of and is still coasting
    // back to its slot. Neither is ever a wreck the timer owns; both are cars
    // somebody is inside or about to be handed.
    if (driving && (driving.fleetHandle === handle || driving.coastHandle === handle)) return;
    const p = parkedSlotOf(handle);
    if (p < 0) return;
    traffic.despawnParked(p);
  };

  // Subscribed only where there is a fleet to retire slots in — hero mode and
  // every dev scene add no handler at all.
  if (traffic && driving) {
    offs.push(bus.on('car:wrecked', (p) => retireWreck(p?.handle ?? -1)));
    offs.push(bus.on('car:despawn', (p) => despawnCar(p?.handle ?? -1)));
  }

  /**
   * A CAR HIT SOMEBODY: THROW THE BLOOD. That is the entire share of the
   * run-over this file owns, and the smallness is the point.
   *
   * `systems/VehicleDamage.js` imports no three.js and never will, so it cannot
   * paint anything — it is the third instance of that file's own "THE MODEL
   * SIGNALS, THE WIRING REMOVES" split, after `'car:wrecked'` (it announces, we
   * retire the slot) and `'car:despawn'` (it announces, we take the car out).
   * Here it announces and we spray.
   *
   * ⚠ THE PAYLOAD CARRIES `byPlayer` AND `fatal` AND THIS HANDLER READS
   * NEITHER, DELIBERATELY. Attribution is already settled inside the damage
   * system, which passes the right `src` into `manager.damage()` — the same
   * argument the auto-attack passes, which is what `attachFactionCombat` gates
   * the provocation and the crime on. A wanted-level or `'crime'` reaction here
   * would be a SECOND place deciding who is to blame for one hit, which is the
   * `hurtPlayer(amount, lethal)` family of bug this file documents against.
   * There is no sound either: the pack ships no body impact (`docs/audio.md`
   * records the two gaps), and borrowing a crash clip for a pedestrian is a
   * worse lie than silence.
   *
   * ⚠ AND IT IS THE GUNSHOT'S SPLASH, NOT A BIGGER ONE. Same call, same chest
   * height, no `strength` — a run-over then reads as the hit the player has
   * already learnt to read, and a second spray tuning nobody has playtested
   * stays uninvented.
   *
   * THE HEIGHT IS THE CHEST, as `fire()`'s `chestY` computes it: the payload's
   * `y` is the victim's FEET (`manager.y` is the ground under an actor), so the
   * splash is lifted half a body — and for the PLAYER, who is not a slot and
   * has no `worldHeight`, by `FIRE.tracerY`, which is the offset
   * `manager.onFire` already uses for exactly that case.
   *
   * ⚠ IT MATCHES `fire()` AND NOT `manager.onFire`, AND THE TWO ALREADY
   * DISAGREE — a pre-existing split this line had to pick a side of, not one it
   * introduced. `fire()` falls back to **1.2** for a type with no
   * `worldHeight`, `manager.onFire` to **1.3**, so the same missing-type actor
   * bleeds from two heights depending on who shot it. 5 cm, invisible, and
   * exactly the drifted-second-copy defect this project keeps finding the
   * expensive way (`PLANT_FRAC`, `ammoPerBox`, `links.js`/`countryroads.js`).
   * The player's own shot is the closer analogue of being run over — both are
   * damage the player is the agent of — so this takes 1.2. Collapsing all three
   * onto one helper is the real fix and belongs with the shot path, not here.
   *
   * ⚠ NOT GATED ON `traffic`, unlike the two subscriptions above: those call
   * into a fleet that may not exist, this one calls into `blood`, which is
   * built unconditionally. In hero mode and every dev scene nothing ever emits
   * on this channel and the handler simply never runs — and it draws no dice of
   * its own in any case (the spray has its own `'blood'` stream), so the golden
   * checksum cannot see it.
   */
  offs.push(bus.on('car:ranover', (p) => {
    if (!p) return;
    const x = p.x;
    const z = p.z;
    if (!Number.isFinite(x) || !Number.isFinite(z)) return;
    // `-1` is the PLAYER, which is not a manager slot — the same convention
    // `manager.onFire`'s `target` uses. The upper bound matters as much as the
    // lower one: a slot past `top` has never been written and `typeOf` on it
    // reads a type nobody spawned.
    const v = p.victim ?? -1;
    const type = v >= 0 && v < manager.top ? manager.typeOf(v) : null;
    const feet = Number.isFinite(p.y) ? p.y : heightAt(x, z);
    const impactY = v >= 0 ? feet + (type?.worldHeight ?? 1.2) * 0.5 : feet + FIRE.tracerY;
    // The car's travel direction, i.e. the way the victim was thrown — the same
    // shooter→victim convention the two shot sites hand the pool, so the spray
    // fans out down the road rather than back at the bumper.
    blood.splash(x, impactY, z, p.dx ?? 0, p.dz ?? 0);
  }));

  // Announce ourselves so the HUD's health bar appears.
  emitHp();
  bus.emit('game:kills', kills);

  // ── per-step scratch (allocated once) ──────────────────────────────────────
  const ctx = { player: null, collision, heightAt, probes, bus, rng: undefined };
  if (fac) {
    // ONE object, built once. `targeting: 'factions'` is the only thing that
    // reaches the manager's faction branches, and hero mode never sets it.
    ctx.targeting = 'factions';
    ctx.hostile = fac.isHostile;
    ctx.playerHostile = (f) => fac.state.isPlayerHostile(f);
  }
  /**
   * The movement axis, read once per step by the SPRINT GATE in step 0 — see
   * there for why the gate has to ask. Its own object rather than a borrowed
   * one: `Player#update` fills its own private `_axis` a few lines later, and
   * two readers sharing one scratch is how a step ends up depending on which of
   * them ran first.
   */
  const runAxis = { x: 0, z: 0 };
  /** Aim rolls only. NEVER the manager's stream — see `systems/accuracy.js`. */
  const aimRng = streamRng(seed, 'aim');
  const shotOut = newShot();
  let fireAcc = 0;
  let respawnAcc = 0;
  /** Edge-detects the death, so `'player:wasted'` fires exactly once per life. */
  let wasDead = false;
  let disposed = false;
  const cosArc = Math.cos((FIRE.arcDeg * Math.PI) / 180);

  /**
   * Nearest live enemy inside the range AND a camera-facing arc.
   *
   * FACTION MODE skips anything whose faction is not currently hostile to the
   * player, and there is no longer any way to opt out of that: the gun answers
   * threats and does nothing else (owner decision, 2026-08-07 — see the
   * header). Hero mode passes no `factions` table, `fac` is null, and this
   * degrades to the pre-faction "nearest live actor" it always was.
   *
   * @param {number} cosHalf Cosine of the arc's HALF angle.
   * @returns {number} Slot id, or -1.
   */
  const pickTarget = (cosHalf) => {
    const px = player.position.x;
    const py = player.position.y;
    const pz = player.position.z;
    // The camera sits behind the player looking along `-(sin yaw, cos yaw)`;
    // "in front of me on screen" is that direction from the player.
    const fx = -Math.sin(rig.yaw);
    const fz = -Math.cos(rig.yaw);
    let best = -1;
    let bestD2 = FIRE.range * FIRE.range;

    for (let i = 0; i < manager.top; i++) {
      const st = manager.state[i];
      if (st === STATE.DEAD || st === STATE.DYING) continue;
      if (fac && !fac.state.isPlayerHostile(manager.team[i])) continue;
      // Y FIRST, and it is not a micro-optimisation: it is the only thing
      // separating a shop interior from the street 100 m above it. See
      // `FIRE.maxDy`.
      const dy = manager.y[i] - py;
      if (dy > FIRE.maxDy || dy < -FIRE.maxDy) continue;
      const dx = manager.x[i] - px;
      const dz = manager.z[i] - pz;
      const d2 = dx * dx + dz * dz;
      if (d2 >= bestD2 || d2 < 1e-6) continue;
      const d = Math.sqrt(d2);
      if ((dx / d) * fx + (dz / d) * fz < cosHalf) continue;
      bestD2 = d2;
      best = i;
    }
    return best;
  };

  /**
   * One auto-attack tick: roll the shot, apply damage only if it connected, and
   * launch the visuals at wherever the round ACTUALLY went.
   *
   * The roll lives here rather than in the manager because hero mode's attack
   * is not a manager weapon — the player is not a slot. It draws from its own
   * `aimRng` for the reason spelled out in `systems/accuracy.js`: nothing may
   * perturb the population stream.
   *
   * @returns {boolean} A ROUND LEFT THE GUN. True for a hit AND for a miss — a
   *   miss is a round that was fired, and it costs ammunition. False means the
   *   trigger was pulled at nothing (no target, or the gun could not answer),
   *   which costs nothing. The caller spends the round on a true and only then;
   *   `Weapons.js#spendRound`'s own doc pins that ordering from the other side.
   */
  const fire = () => {
    // ⚠ THE AMMUNITION GATE COMES FIRST, BEFORE THE SCAN. A safe, empty or
    // reloading gun does not even look for a target: `pickTarget` is an O(top)
    // walk of the whole pool, and running it to throw the answer away is both
    // waste and a lie — the tracer, the blood and the alarm below all hang off
    // a round that was never going to leave the barrel.
    if (weapons && !weapons.canFire()) return false;
    // ONE trigger. There is no manual shot: the player answers threats and
    // never opens fire (owner decision, 2026-08-07 — the deliberate SPACE
    // attack is DELETED, not disabled, and Space is now only the handbrake).
    // ⚠ Ammunition is NOT spent here and must not be — see the return contract:
    // the round is billed by the caller, after this returns true.
    const i = pickTarget(cosArc);
    if (i < 0) return false;
    const px = player.position.x;
    const pz = player.position.z;
    const ex = manager.x[i];
    const ez = manager.z[i];
    const type = manager.typeOf(i);
    const shot = rollShot(aimRng, shotOut, px, pz, ex, ez, FIRE.range, FIRE.accuracy);

    // Chest height, from the victim's FEET — `manager.y` is the ground under it.
    const chestY = manager.y[i] + (type?.worldHeight ?? 1.2) * 0.5;
    const died = shot.hit && manager.damage(i, FIRE.damage, ex - px, ez - pz, fac?.PLAYER);

    tracers.fire(
      px,
      player.position.y + FIRE.tracerY,
      pz,
      shot.x,
      shot.hit ? chestY : heightAt(shot.x, shot.z) + FIRE.missY,
      shot.z,
    );
    // Blood ONLY on a connect — a miss that bleeds is worse than no feedback.
    if (shot.hit) blood.splash(ex, chestY, ez, ex - px, ez - pz);
    // THE BANG, on the same path the round is billed on: a hit AND a miss are
    // both a round that left, and both make a noise. A trigger pull at nothing
    // returned above and is silent, which is the same contract `spendRound()`
    // is held to two frames of code away.
    audio?.playerShot();
    if (died) {
      kills++;
      bus.emit('game:kills', kills);
    }
    // Gunfire scatters the street whether or not it hit anything — at the
    // muzzle AND where the round landed, because both are things a bystander
    // hears or sees.
    if (population) {
      population.alarm(px, pz, PED_TUNING.shotRadius);
      population.alarm(shot.x, shot.z, PED_TUNING.shotRadius);
    }
    return true;
  };

  // ── HUD blips (additive, read-only — see the BUS CONTRACT note) ────────────
  // Everything below is allocated ONCE. The emit reuses the payload, the array
  // and all 64 entry objects; at 8 Hz for the life of a session that is the
  // difference between zero garbage and ~500 objects a second.
  /** Faction per TYPE index. `types` never changes after construction. */
  const blipKindOf = types.map((t) => blipKindForId(t?.id));
  /** Sorted-ascending scratch for the nearest-first selection. */
  const blipD2 = new Float32Array(BLIPS.max);
  const blipSlot = new Int32Array(BLIPS.max);
  /** @type {Array<{x: number, z: number, kind: string}>} Reused entries. */
  const blipPool = Array.from({ length: BLIPS.max }, () => ({ x: 0, z: 0, kind: 'civilian' }));
  /** @type {Array<{x: number, z: number, kind: string}>} Reused, re-`length`ed. */
  const blipList = [];
  /** The occupied car, appended past the enemy window. Reused like the rest. */
  const carBlip = { x: 0, z: 0, kind: 'car' };
  /**
   * THE RESUPPLY DRONE, appended the same way and for the same no-litter
   * reason. `'drone'` is a REAL key in `BLIP_COLORS` (`ui/hud/mapRender.js`,
   * amber `#f59e0b`, the same hex as the machine's own slung crate and its
   * ground ring) — the HUD side already knows the kind, so there is nothing to
   * fall back to here and no default worth writing.
   */
  const droneBlip = { x: 0, z: 0, kind: 'drone' };
  /**
   * MISSION MARKERS, on the same channel as the blips because they are the same
   * question ("what is on my map right now") and a second 8 Hz channel would be
   * a second subscription in every consumer. It is the DIRECTOR's own reused
   * array — the no-litter rule applies twice over; see the header.
   * @type {Array<any>}
   */
  const markerList = [];
  /**
   * ⚠ `player.driving` RIDES THIS CHANNEL RATHER THAN GETTING ONE OF ITS OWN.
   * It is a STATE, it changes at walking pace, and every consumer that could
   * want it is already subscribed here for the position it goes with — a
   * dedicated `'player:car'` edge channel would be a second subscription in
   * every one of them plus a replay problem the HUD mounts straight into (the
   * bus has no replay, so a HUD that mounted mid-drive would think the player
   * was on foot until they got out). The payload is the WHOLE state every time,
   * exactly like `'player:ammo'`, so a subscriber that missed an event is still
   * correct on the next one.
   */
  const blipPayload = { player: { x: 0, z: 0, yaw: 0, driving: false }, blips: blipList, markers: markerList };
  /** The player's car as a HAZARD for the population director. Reused. */
  const hazard = { x: 0, z: 0, speed: 0, on: false };
  /** `worldMarkers()`'s output array — reused, like everything else here. */
  const markerScratch = [];
  let blipAcc = 0;
  /** Sticky map heading — see `BLIPS.moveEps`. */
  let blipYaw = 0;

  /**
   * One `'hud:blips'` emit: the player plus the nearest {@link BLIPS.max} live
   * enemies. A straight linear scan over the SoA with an insertion into a
   * 64-wide sorted window; the `d2 >= worst` early-out means the inner shift
   * almost never runs once the window is full.
   */
  const emitBlips = () => {
    // Anchored at THE FOCUS (see step 1a), so a player under the city still has
    // a minimap of the street their door opens onto — both the chevron and the
    // nearest-first window, or the window would rank the whole island by its
    // distance from a room in the slot region and the map would be a lottery.
    //
    // ⚠ COPY the fields. `interiors.focus` is that system's own reused scratch
    // and `blipPayload.player` is ours; aliasing one into the other would hand
    // every subscriber an object two systems mutate behind its back.
    const f = interiors?.focus ?? player.position;
    const px = f.x;
    const pz = f.z;
    const vx = player.velocity.x;
    const vz = player.velocity.z;
    if (vx * vx + vz * vz > BLIPS.moveEps) blipYaw = Math.atan2(vx, vz);
    blipPayload.player.x = px;
    blipPayload.player.z = pz;
    blipPayload.player.yaw = blipYaw;
    blipPayload.player.driving = !!driving?.inCar;

    let n = 0;
    for (let i = 0; i < manager.top; i++) {
      if (!manager.isLive(i)) continue;
      // A car's occupants are not their own blips: the CAR is the thing on the
      // map, and three dots stacked on one vehicle reads as a crowd.
      if (manager.brain[i] === BRAIN.SEATED) continue;
      const dx = manager.x[i] - px;
      const dz = manager.z[i] - pz;
      const d2 = dx * dx + dz * dz;
      if (n === BLIPS.max && d2 >= blipD2[n - 1]) continue;
      let j = n < BLIPS.max ? n : BLIPS.max - 1;
      while (j > 0 && blipD2[j - 1] > d2) {
        blipD2[j] = blipD2[j - 1];
        blipSlot[j] = blipSlot[j - 1];
        j--;
      }
      blipD2[j] = d2;
      blipSlot[j] = i;
      if (n < BLIPS.max) n++;
    }

    for (let k = 0; k < n; k++) {
      const i = blipSlot[k];
      const e = blipPool[k];
      e.x = manager.x[i];
      e.z = manager.z[i];
      e.kind = blipKindOf[manager.typeIdx[i]] ?? 'civilian';
      blipList[k] = e;
    }
    let total = n;
    if (driving?.inCar) {
      // Appended, never displacing an enemy: the nearest-first window is a
      // threat list, and the car you are sitting in is not a threat.
      carBlip.x = driving.body.x;
      carBlip.z = driving.body.z;
      blipList[total++] = carBlip;
    }
    if (drone?.active) {
      // AFTER the car and, like it, APPENDED — never displacing an enemy. The
      // nearest-first window above is a THREAT list, and a delivery flying in
      // to hand you five clips is the opposite of a threat: dropping the
      // gunman standing behind you to draw the drone would be exactly the
      // wrong sixty-fourth entry.
      droneBlip.x = drone.x;
      droneBlip.z = drone.z;
      blipList[total++] = droneBlip;
    }
    blipList.length = total;
    // The WHOLE marker set, not the near ones the ring batch draws: the map is
    // exactly what long-range navigation is for.
    markerList.length = 0;
    if (missions) for (const mk of missions.markers()) markerList.push(mk);
    bus.emit('hud:blips', blipPayload);
  };

  const sys = {
    name: 'gameplay',
    /** @type {Player} */ player,
    manager,
    spawner,
    rig,
    input,
    blobs,
    spriteRenderer,
    rigRenderer,
    /** @type {Traffic|null} Null on a level with no `map.roads`. */
    traffic,
    /** @type {ReturnType<typeof createDriving>|null} */
    driving,
    /** @type {ReturnType<typeof createCarjack>|null} Null on a level with no roads. */
    carjack,
    /** @type {ReturnType<typeof createInteriors>|null} Null without `level.doors`. */
    interiors,
    /** THE PLAYER'S MONEY. Always built — see its construction note. */
    wallet,
    /**
     * @type {ReturnType<typeof createStamina>} THE SPRINT BAR. Always built,
     * for the wallet's reason — so unlike `weapons` and `shops` this one is
     * never null. Exposed so `DebugPanel` and the dev scene can read the bar
     * without keeping a second copy of it; a getter for symmetry with those
     * two, though it is assigned once and never reassigned.
     */
    get stamina() {
      return stamina;
    },
    /** @type {ReturnType<typeof createShops>|null} Null without `interiors`. */
    get shops() {
      return shops;
    },
    /**
     * @type {ReturnType<typeof createWeapons>|null} THE GUN'S MAGAZINE. Null
     * without a `weapons` table — every dev scene and every suite. Exposed so
     * `DebugPanel` and `/play/weapons` can read the clip and drive the stance
     * without a second copy of the state; a getter for symmetry with `shops`,
     * though this one is assigned once and never reassigned.
     */
    get weapons() {
      return weapons;
    },
    /** @type {ReturnType<typeof createPopulation>|null} Null without factions. */
    get population() {
      return population;
    },
    /** @type {ReturnType<typeof createWanted>|null} Null without factions. */
    wanted,
    /** @type {ReturnType<typeof createBreakables>|null} Null without the data. */
    breakables,
    /** @type {ReturnType<typeof createVehicleDamage>|null} Null without roads. */
    get vehicles() {
      return vehicles;
    },
    /** @type {ReturnType<typeof createMissions>|null} Null without a script. */
    get missions() {
      return missions;
    },
    /**
     * @type {ReturnType<typeof createPhone>|null} THE PHONE. Null without a
     * mission script AND without a weapon table. Exposed for `/play/phone` and
     * `DebugPanel` the same way `shops` and `weapons` are — and note there is
     * deliberately NO `drone` accessor beside it: the phone is the drone's only
     * shipped caller and takes it by direct injection in the same closure, so a
     * second public door onto the drone would be a surface nobody asked for.
     */
    get phone() {
      return phone;
    },
    /**
     * @type {ReturnType<typeof createGameAudio>|null} THE WIRING. Null without
     * a sound table. Exposed so the HUD can reach it the way `PauseMenu.jsx`
     * already reaches this system (`engine.systems.find(s => s.name ===
     * 'gameplay')`) — the volume sliders and the mute toggle live there.
     */
    get audio() {
      return audio;
    },
    /**
     * @type {ReturnType<typeof createSfx>|null} THE MACHINE, beside the wiring
     * that drives it, because the two readings a settings screen needs —
     * `setLevel`/`setMuted`/`unlock` — are the facade's, not the wiring's.
     */
    get sfx() {
      return sfx;
    },
    /**
     * @type {ReturnType<typeof createRadio>|null} THE STATION. Null without a
     * radio table. Exposed for `DebugPanel` and a future dial the way `phone`
     * and `weapons` are — and note that `setStation` is the only mutator on it,
     * so a HUD that grew a tuner would still not be a second place deciding what
     * plays: `GameAudio` stays the only subscriber that makes a noise.
     */
    get radio() {
      return radio;
    },
    /** @type {import('./Population.js').FactionRules|null} */
    factions: fac,
    /** @returns {number} */
    get kills() {
      return kills;
    },

    /**
     * One fixed step. See the order note in the file header.
     * @param {number} dt
     */
    update(dt) {
      if (disposed || !(dt > 0)) return;

      // 1. player + camera. The spin is applied BEFORE the follow so the whole
      // frame — movement basis, spawner cone, sprite rows — sees one yaw. The
      // drag is drained in the same breath, for the same reason; the two are
      // additive, so a drag during a Q/E hold simply adds to it.
      const shift = input.down('ShiftLeft') || input.down('ShiftRight');
      rig.spinYaw(shift ? 0 : (input.down('KeyE') ? 1 : 0) - (input.down('KeyQ') ? 1 : 0), dt);
      rig.rotateYawBy(drag.consume());
      // In a car, WASD drives it and the player is carried; on foot, `driving`
      // only publishes where the pedestrian is standing (traffic brakes for it).
      // Mid-sequence the player is neither: the carjack machine is walking
      // them to the door, so their own movement is suspended. `driving` still
      // ticks — it is what publishes the pedestrian the traffic brakes for,
      // and it owns the two-phase clock the sequence is waiting on.
      // ...and a WASTED player is a fourth non-mover: for the three seconds
      // between the splash and the respawn they are a body on the tarmac, not a
      // chibi who can still walk it off.
      // ...and a FIFTH: a player walking through a door. `interiors.locked`
      // covers the whole fade-out/swap/fade-in, and for most of it there is
      // nothing under their feet to walk on anyway — the swap teleports them 40
      // m under the city. Once they are STANDING in a room `locked` is false
      // again and they walk it normally, on the room's own grid and floor.
      //
      // ⚠ THE FIVE NON-MOVERS ARE NAMED ONCE, HERE, and the sprint gate below
      // reads the SAME expression rather than a second differently-worded copy
      // of it. Two lists of who counts as "on foot" is the
      // `hurtPlayer(amount, lethal)` family of bug: they agree until somebody
      // adds a sixth.
      const onFoot = !driving?.inCar && !interiors?.locked && !carjack?.locked && !player.dead;

      // 0. THE SPRINT. Numbered 0 because it runs BEFORE the mover — the
      // multiplier is spent by the very step it gates — and written inside step
      // 1's block because it shares that one `shift` read above.
      //
      // `wants` is three conditions and every one of them earns its place:
      //  • the run key is HELD (`shift`, already read above for the yaw snap —
      //    one read, so the two cannot disagree within a step);
      //  • the player is actually ASKING TO MOVE. ⚠ Without this, standing still
      //    on a street corner with Shift held drains the whole bar and the
      //    player's first step is a walk — the bug this line closes. The test is
      //    `Player#update`'s own `moving` test on the same axis (the yaw
      //    rotation it applies is a rotation, so it preserves length exactly);
      //  • they are ON FOOT. A car has its own limiter and a player mid-carjack,
      //    mid-door or dead is not running anywhere.
      //
      // ⚠ BOTH CALLS ARE UNCONDITIONAL. `stamina.update` ticks on every path
      // because `wants: false` is HOW THE BAR REGENERATES — the `Weapons#update`
      // precedent, "a reload is a CLOCK, not an action" — and `speedMul` is
      // ASSIGNED on every path so a 1.4 latched the instant before the player
      // got into a car cannot survive into the step after they get out of it.
      const ax = input.axis(runAxis);
      stamina.update(dt, shift && onFoot && Math.hypot(ax.x, ax.z) > 1e-4);
      player.speedMul = stamina.speedMul;

      if (onFoot) player.update(dt);
      carjack?.update(dt);
      interiors?.update(dt);
      driving?.update(dt);
      rig.follow(player.position, player.velocity, dt);

      // 1a. THE FOCUS — where the WORLD thinks the player is, which is not
      // always where the player is. Every ring system below follows this rather
      // than `player.position`: traffic's activation ring, the population's
      // pavements, the spawner's cone, the police response and the minimap.
      // While the player is under the city (or on their way in or out) it is
      // the SHOPFRONT's outdoor ring — otherwise the street empties the moment
      // you step inside for a sandwich and is still empty when you come out.
      // Outdoors `interiors.focus` is null and this is the player, exactly as
      // it always was. It carries NO y: nothing downstream reads one.
      const focus = interiors?.focus ?? player.position;

      // 1b. traffic. After the camera so the focus ring and the spawn cone are
      // this frame's, before the enemies because nothing they do can move a car.
      if (traffic) {
        // The fleet culls per INSTANCE (99.9% of car parts are off screen —
        // see `CarFleet.js`'s header), and this is the one point in the step
        // where the camera is already this frame's and the cars have not moved
        // yet: `rig.follow` has just run `Engine#updateCamera`, and
        // `traffic.update` calls `fleet.update()` at the end of its own step.
        // So there is no frame of lag to compensate for — `CULL.guard` covers
        // the sub-metre a fast car travels between here and that write, and
        // an open door.
        carFleet?.setCamera(engine.camera);
        // The smoke is billboarded off the same camera and at the same point in
        // the step, for the same reason: it is this frame's camera and the
        // particles have not moved yet.
        vehicleFx?.setCamera(engine.camera);
        traffic.setFocus(focus.x, focus.z, rig.yaw);
        traffic.update(dt);
        // Live received shadow (`docs/sun.md`). Injected by the level like
        // everything else the fleet touches; draw state only, no rng, so the
        // sim cannot see it. Skipped whole at night — the day channel the
        // shade multiplies is crossfaded out anyway.
        if (level?.sunShade?.active?.()) carFleet?.updateSunShade(dt, level.sunShade.sample);
      }

      // 1c. street furniture the cars have been through. AFTER the driving step
      // so the hit test sees where the car IS rather than where it was — a lamp
      // that goes over a frame late at 17 m/s goes over 28 cm behind the bumper.
      // A COASTING car counts: the player bailed out of it, it is still moving,
      // and a driverless car that ploughs through a bench and leaves it standing
      // is the sim disagreeing with itself.
      if (breakables) {
        breakCars.length = 0;
        if (driving?.inCar) breakCars.push(driving.body);
        if (driving?.coastBody) breakCars.push(driving.coastBody);
        breakables.update(dt, breakCars);
      }

      // 1d. VEHICLE DAMAGE. Every neighbour of this line is load-bearing:
      //
      //  • AFTER the driving step (1) and the breakables (1c), because both are
      //    where an impact HAPPENS. `Driving` reported this step's crash into
      //    `onImpact` a few lines ago and the reading is only valid until the
      //    next `resolveCarBody` overwrites it, so the damage has to be spent
      //    on the step it was measured — a frame of lag here is a car that
      //    catches fire in the wrong place, or a shunt that is billed twice
      //    because the second frame still sees the first frame's `impactDv`;
      //  • BEFORE the enemies (2), because a car that exploded on this step has
      //    to have dealt its blast damage BEFORE the manager runs. Otherwise a
      //    slot the blast killed still takes its turn — it shoots, it moves,
      //    it emits — and dies a step later, which reads as the explosion
      //    missing somebody standing in it.
      //  • AND THE RUN-OVER IS INSIDE THIS CALL FOR BOTH REASONS AT ONCE, which
      //    is why it is not a step of its own somewhere earlier. The test is a
      //    car pose against a pedestrian pose, and neither is settled until the
      //    driving step (1) has moved the player's car and `traffic.update`
      //    (1b) has moved every AI one — run it before either and a pedestrian
      //    is struck by a bumper that is still a frame behind, which at 17 m/s
      //    is 28 cm of car (the same arithmetic 1c records for the bench it
      //    ploughs through). And the victim it damages is a slot the manager
      //    has not stepped yet, so a fatal hit is dead before its turn rather
      //    than walking one last stride out of the road it was killed in.
      //
      // It sits outside the `if (traffic)` block above only because it is null
      // whenever traffic is; there is no second gate.
      vehicles?.update(dt);

      // 2. enemies. The provocation timers run FIRST, so a crew whose 45 s
      // expired this step is already calm when the manager asks.
      fac?.state.update(dt);
      ctx.player = player;
      manager.update(dt, ctx);

      // 2a. THE SHOP, and it is HERE for one reason: `manager.update` has just
      // written `y[i] = ctx.heightAt(x, z)` for every actor from the OUTDOOR
      // terrain, which would drag a shop's crowd from the room's floor at −100
      // up to street level and draw them standing inside the building. Shops.js
      // pins `y` back to `plan.floorY` every step — but a pin is only the last
      // word if it runs AFTER the write, so this call cannot sit next to
      // `interiors.update` (step 1) where it reads more naturally. It still sees
      // THIS step's plan: `interiors.update` ran above, so the identity watch
      // populates the room on the step the player walks in, not the next one.
      //
      // ⚠ KNOWN EDGE, and it is the same `ctx`: the manager resolves every
      // actor against ONE outdoor collision grid, so a shop customer is tested
      // against the CITY's colliders rather than the room's walls and could in
      // principle walk out through one. Measured instead of assumed
      // (`tests/shops.test.mjs` (p)): over 20 s a crowd never leaves the box its
      // own stand points describe, because the only thing steering it is a goal
      // 1.5 m away inside the same room — nothing else hands these slots an
      // errand (the population director only drives slots its `owned[]` byte
      // marks, and ours are not). What is NOT covered by that measurement is a
      // city collider that happens to overlap a room's slot and pushes somebody
      // out; if a customer is ever seen leaving, that is the cause.
      //
      // Making `ctx` per-actor is NOT the fix to reach for — that object is on
      // the path hero mode's golden checksum pins.
      shops?.update(dt);

      // 2b. the ambient source. Exactly one of these runs: the population
      // director in a faction city, the hostile ring spawner everywhere else.
      if (population) {
        if (driving?.inCar) {
          hazard.x = driving.body.x;
          hazard.z = driving.body.z;
          hazard.speed = driving.body.speed;
          hazard.on = true;
        } else {
          hazard.on = false;
        }
        population.update(dt, focus, rig.yaw, hazard);
      } else {
        spawner.update(dt, focus, rig.yaw);
      }

      // 2c. the wanted level. AFTER the population, because a police unit
      // spawned this step reads the pavement graph and the car seats the
      // director has just settled; before the shot, because a star that expired
      // this step must not still be hostile when the trigger is pulled.
      wanted?.update(dt, focus, rig.yaw);

      // 2d. missions. AFTER the wanted level, because a mission that spawns a
      // crew this step wants to do it into a world whose police response has
      // already reconciled; and after the population for the same reason it
      // was built after it. Before the shot, because a mission that FAILED on
      // this step must not have its already-despawned enemies shot at.
      missions?.update(dt);

      // 3. auto-attack (fixed cadence, independent of frame rate). Suppressed
      // in a car — see the header: there is no drive-by yet.
      //
      // ⚠ NOT suppressed indoors, and that is still a DECISION — but the edge
      // it used to carry is now closed. `pickTarget` reads `player.position`
      // (the real one, 100 m under the city) and its range test is XZ-ONLY, as
      // the manager's own `_acquire` still is, so the 100 m drop bought nothing
      // and the interior slot grid sits UNDER the island's SW quadrant rather
      // than off in the sea. THE FOCUS was the whole defence: every ambient
      // source spawns and culls around the shopfront, so the ground above a
      // room is empty of anything the player did not leave there — which was
      // never a proof, and stopped being good enough when `FIRE.range` went
      // 9 -> 14 and swallowed a planted officer's 10.8 m standoff. The Y test
      // the comment here always named is now in `pickTarget`; see
      // {@link FIRE.maxDy}.
      //
      // ⚠ THE RELOAD CLOCK IS OUTSIDE THE GATE, ON PURPOSE. A reload is a
      // CLOCK, not an action, and a clock parked behind "may the player shoot"
      // stops in a car and under an interiors fade — you get "my gun never
      // reloaded because I walked through a door", and it comes back a minute
      // later still at 1.5 s remaining. The asymmetry is the feature and
      // `Weapons.js`'s header states the same rule from the other side: you can
      // reload in a car; you cannot shoot from one.
      //   ⚠ AN OPEN SHOP PANEL IS NOT ONE OF THE CASES THIS FIXES, and it would
      // be easy to read it as one. The panel sets the time scale to 0, so
      // `Level#update` is not called at all (measured 0 calls/s open against
      // 102 closed, `docs/shops.md`) — the clock stops there wherever this line
      // sits, and that is correct: the whole sim is frozen, not just the gun.
      // `Weapons#addAmmo` publishes SYNCHRONOUSLY for exactly that reason.
      weapons?.update(dt);
      if (!player.dead && !driving?.inCar) {
        // ⚠ READ `weapons.interval` DIRECTLY — never `weapons?.interval ??
        // FIRE.interval`. The getter deliberately returns `Infinity` (NEVER 0)
        // when no weapon resolves, so a malformed content table makes the gun
        // inert instead of hanging the tab in the loop below; `??` catches only
        // null/undefined and would pass that Infinity through anyway, but it
        // reads as a fallback that is doing something, and the next reader
        // "tidying" it into a `||` reintroduces the 0.
        const interval = weapons ? weapons.interval : FIRE.interval;
        fireAcc += dt;
        // ⚠ `if`, NOT `while`, AND THAT IS A DELIBERATE BEHAVIOUR CHANGE — not
        // a refactor. The `while` was frame-hitch CATCH-UP: bank three
        // intervals during a stall and it fires three times on the recovery
        // frame. That was invisible while the step was fixed and the ammunition
        // infinite; the moment a clip exists it is "the game hitched and ate
        // three bullets", which is unanswerable because the player never pulled
        // the trigger. `EnemyManager._stepFire` solved exactly this once already
        // — single shot plus a one-interval clamp, with the reasoning in its own
        // comment ("a unit that spent a minute out of range would empty a
        // magazine the instant it closed") — and this is the two files being
        // brought into line. The clamp is what stops the accumulator itself
        // banking a burst; the safety catch does NOT suppress it, so going hot
        // never unloads one either (`Weapons.js`, same rule, its side).
        if (fireAcc >= interval) {
          fireAcc -= interval;
          if (fireAcc > interval) fireAcc = interval;
          // The round is billed HERE and only on a true: a trigger pull at
          // nothing is free, a miss is not. See `fire()`'s return contract.
          if (fire()) weapons?.spendRound();
        }
      }
      tracers.update(dt);
      blood.update(dt);

      // 3b. WASTED → respawn. Three beats, and `ui/hud/Wasted.jsx` is driven by
      // the first and the last of them: the splash goes up on `'player:wasted'`
      // and comes down on `'player:respawn'`, so the HUD keeps no clock and can
      // never disagree with the sim about how long you were dead.
      if (player.dead) {
        if (!wasDead) {
          wasDead = true;
          // Where they FELL, not where they will wake up — a death marker on
          // the minimap wants the former and the splash wants neither, but a
          // channel that carries the useful one costs nothing.
          bus.emit('player:wasted', { x: player.position.x, z: player.position.z });
        }
        respawnAcc += dt;
        if (respawnAcc >= FIRE.respawnDelay) {
          respawnAcc = 0;
          wasDead = false;
          // Dying in a car leaves the car where it stopped, not in orbit around
          // the respawn point. `force` skips the speed gate — the alternative is
          // a corpse doing 17 m/s that can never get out.
          driving?.exitCar(true);
          // THE SILENCE. Heat to zero, every provocation timer with it, and the
          // whole police response stood down where it stands — you got away by
          // dying, which is the oldest deal in the genre.
          wanted?.reset();
          // ...and the gun comes back LOADED AND HOT: `onRespawn()` refills the
          // CLIP for free, clears any half-finished reload and takes the safety
          // off. THE RESERVE IS LEFT EXACTLY WHERE IT WAS.
          //
          // ⚠ `respawnFloor` IS DELETED — do not go looking for it, and do not
          // reinstate it. It was a documented STOPGAP against one dead end:
          // out of ammo AND out of money, with the shop the only door
          // ammunition had into the world. THE RESUPPLY DRONE (step 3c) closes
          // that dead end AT THE SOURCE — the reserve going below one clip is
          // now answered while the player is still alive, which is the real fix
          // — and a floor that quietly refilled on death was the paper over it.
          // Worse, it made dying a resupply, which is the opposite of what a
          // death should be worth.
          //
          // ⚠ WHAT SURVIVES IS THAT THE GUN DOES NOT MATCH THE VEST a few lines
          // below, which IS stripped, and that asymmetry is deliberate. A
          // player who wakes up with an empty magazine — or worse on SAFE,
          // because that is where they left it — does not read "I lost my
          // ammo": they read "my gun is broken", which is the same class of
          // report the 9 m `FIRE.range` bug produced on 2026-08-07 and cost a
          // playtest to diagnose. A respawn is a RESET, not a resupply.
          weapons?.onRespawn();
          // ...and the legs come back FRESH. A full bar costs the player
          // nothing they had banked (unlike the vest below, which is stripped):
          // stamina is not a possession, it is a condition, and waking up in
          // the plaza already winded is the "my gun is broken" report above
          // wearing different clothes.
          stamina.onRespawn();
          const r2 = FIRE.respawnClearR * FIRE.respawnClearR;
          for (let i = 0; i < manager.top; i++) {
            if (!manager.isLive(i)) continue;
            // Never pull somebody out of a moving car: that leaves an empty
            // driving seat on screen, which is worse than a witness.
            if (manager.brain[i] === BRAIN.SEATED) continue;
            // In a faction city the clear is for THREATS only. After the reset
            // above nothing is hostile, so the ambient street the player wakes
            // up on is untouched — which is the whole difference between "you
            // respawned" and "the district was deleted".
            if (fac && !fac.state.isPlayerHostile(manager.team[i])) continue;
            const dx = manager.x[i] - spawn.x;
            const dz = manager.z[i] - spawn.z;
            if (dx * dx + dz * dz < r2) manager.despawn(i);
          }
          player.moveTo(spawn.x, spawn.z);
          // THE VEST DOES NOT SURVIVE DEATH. `hurt(-n)` deliberately never
          // touches armour (healing and armour are different purchases), so the
          // respawn is the one place that strips it — you wake up in the plaza
          // with your health back and nothing else.
          player.clearArmour?.();
          player.hurt(-player.maxHp);
          emitHp();
          rig.snapTo(player.position);
          bus.emit('player:respawn', { x: spawn.x, z: spawn.z });
        }
      } else {
        respawnAcc = 0;
        wasDead = false;
      }

      // 3c. the resupply drone. It CREDITS AMMUNITION, so it is a sim mutation
      // and it belongs in the numbered steps — never down in step 5 with the
      // draw state, which is a pure read of a settled frame.
      //
      // The slot is exact, and both halves of it are load-bearing:
      //  • AFTER step 3 (the gun). The round that empties the reserve is billed
      //    by `weapons.spendRound()` up there and `'player:ammo'` is published
      //    SYNCHRONOUSLY on that same call, so by the time this line runs the
      //    subscriber inside `Drone.js` has already seen the crossing and armed
      //    a sortie. Step this first and every delivery is ordered one step
      //    late — invisible at 1/60, and exactly the kind of ordering debt that
      //    turns into "the drone sometimes takes an extra second" later.
      //  • AFTER step 3b (the respawn). A player who died and came back on THIS
      //    step has their position, their clip and their stance settled by the
      //    block above; the drone's own defer asks `player.dead`, and running
      //    before 3b would have it read a corpse that is already back on its
      //    feet — it would turn round and re-arm for a death that no longer
      //    exists.
      //
      // ⚠ UNCONDITIONAL, the `Weapons.js` rule: the cooldown is a CLOCK, not an
      // action. The system's own guards decide whether anything flies.
      drone?.update(dt);

      // 4. contact shadows: one instanced quad per actor, ONE commit
      for (let i = 0; i < manager.top; i++) {
        const id = blobOf[i];
        if (id < 0) continue;
        if (manager.state[i] === STATE.DEAD) continue;
        const t = manager.typeOf(i);
        const s = manager.scale[i];
        const lift = manager.wobbleBob[i];
        // A radius-0 blob draws nothing. Somebody sitting in a car casts no
        // contact shadow — the car's own is already under them, and a second
        // puddle sliding along the road at 12 m/s is the tell.
        const seated = manager.brain[i] === BRAIN.SEATED;
        blobs.set(
          id,
          manager.x[i],
          manager.y[i],
          manager.z[i],
          seated ? 0 : (t?.blobR ?? 0.45) * s - lift * 0.6,
          0.9 * s,
        );
      }
      if (playerBlob >= 0) {
        const lift = player.wobble.bob;
        // A radius-0 blob draws nothing: in a car the chibi is hidden, and a
        // contact shadow with nobody standing on it is a puddle following the
        // bonnet around.
        blobs.set(
          playerBlob,
          player.position.x,
          player.position.y,
          player.position.z,
          driving?.inCar ? 0 : 0.42 - lift * 1.2,
          1 - lift * 3,
        );
      }
      blobs.commit();

      // 4b. mission markers. A per-frame REBUILD, not a slot pool (see
      // `render/MissionMarkers.js`), and it draws only the NEAR set — the map
      // gets the rest. The pulse rides the manager's own elapsed clock so every
      // ring in view breathes in step.
      if (markerBatch && missions) {
        markerBatch.reset();
        for (const mk of missions.worldMarkers(markerScratch)) {
          const tint = MARKER.tints[mk.kind] ?? MARKER.tints.objective;
          let a = mk.dim ? MARKER.dim : 1;
          if (mk.tracked) a *= MARKER.trackedBoost;
          markerBatch.add(mk.x, heightAt(mk.x, mk.z), mk.z, mk.r, tint, a);
        }
        markerBatch.commit(manager.elapsed);
      }

      // 4c. door rings. Same per-frame rebuild, same clock (so a door ring and
      // a mission ring breathe together rather than reading as two effects),
      // and the near set is chosen NEAREST-FIRST rather than in level order:
      // a parade of shopfronts can put more than `DOORS.max` doors inside
      // `showR`, and dropping the one you are standing on to draw one down the
      // block is exactly the wrong sixteen.
      //
      // Skipped entirely while INSIDE — the rings live on the pavement 100 m
      // overhead, and a marker that ignores depth would paint them through the
      // ceiling of the room the player is standing in.
      if (doorMarkers) {
        doorMarkers.reset();
        if (!interiors?.inside) {
          const px = player.position.x;
          const pz = player.position.z;
          const r2 = DOORS.showR * DOORS.showR;
          let n = 0;
          for (let i = 0; i < levelDoors.length; i++) {
            const ring = levelDoors[i]?.ring;
            if (!ring) continue;
            const dx = ring.x - px;
            const dz = ring.z - pz;
            const d2 = dx * dx + dz * dz;
            if (d2 > r2) continue;
            if (n === DOORS.max && d2 >= doorD2[n - 1]) continue;
            let j = n < DOORS.max ? n : DOORS.max - 1;
            while (j > 0 && doorD2[j - 1] > d2) {
              doorD2[j] = doorD2[j - 1];
              doorIdx[j] = doorIdx[j - 1];
              j--;
            }
            doorD2[j] = d2;
            doorIdx[j] = i;
            if (n < DOORS.max) n++;
          }
          for (let k = 0; k < n; k++) {
            const ring = levelDoors[doorIdx[k]].ring;
            doorMarkers.add(
              ring.x,
              heightAt(ring.x, ring.z),
              ring.z,
              DOORS.radius,
              DOORS.tint,
              DOORS.alpha,
            );
          }
        }
        doorMarkers.commit(manager.elapsed);
      }

      // 5. draw state. BOTH renderers sync every frame: the per-type masks
      // decide which one draws each creature, so a level mixing sprite
      // creatures and rig creatures needs both populated. Each sync is a
      // linear pass over the pool writing only its masked-in types. The rig
      // sync also runs each unit's pose/update, so it gets the step dt and the
      // player (hero mode's one and only target) for `update()`'s unit view.
      //
      // The depth bias goes first and it belongs HERE rather than beside the
      // camera: it is pure draw state (no sim field reads it, it draws no rng),
      // it needs the yaw the rig SETTLED on this step, and the player's own
      // sprite has to be re-synced against a position `update()` has already
      // finished writing.
      syncDepthBias();
      spriteRenderer.sync(manager, { camYaw: rig.yaw });
      rigRenderer.sync(manager, { camYaw: rig.yaw, dt, player, targeting: 'player' });

      // ── 5a. the drone's pose ──────────────────────────────────────────────
      // Pure draw state, exactly like the depth bias above and the bubbles
      // below: it READS the position step 3c finished writing, it writes no sim
      // field and it draws no rng, so it can only sit between the last write
      // and the input flush. `drone.active` is the renderer's whole gate — the
      // machine is IDLE and nowhere for most of a session, and `setVisible`
      // takes the ground ring with it in the same call.
      //
      // ⚠ `DRONE.spin` is PASSED THROUGH rather than copied into this file.
      // `DroneMesh#update` takes the rate as an argument for precisely that
      // reason: a second copy of the number here is the `links.js` /
      // `countryroads.js` mirroring trap, where two constants that must agree
      // live in two files and nothing makes them move together.
      //
      // ⚠ `update` IS CALLED EVERY FRAME, HIDDEN OR NOT, and the pose/visibility
      // are what the `active` gate covers. That is `DroneMesh#update`'s own
      // stated contract and the reason is a phase one: the mission rings (4b)
      // and the door rings (4c) breathe on `manager.elapsed`, while the drone's
      // ring rides the private sum of the `dt`s it was handed, so a frame
      // skipped while the machine is IDLE is a frame that clock never gets back
      // — and the drone would arrive with its ring pulsing out of step with
      // every other ring in the view, which reads as three unrelated effects
      // rather than one rule. The hidden path is a `reset()` + `commit()` on an
      // empty batch and costs nothing. Visibility and pose are set FIRST so the
      // ring the rebuild emits is this frame's, not last frame's.
      if (droneMesh && drone) {
        const on = drone.active;
        droneMesh.setVisible(on);
        if (on) droneMesh.setPose(drone.x, drone.y, drone.z, drone.yaw);
        droneMesh.update(dt, DRONE.spin);
      }

      // ── 5c. speech bubbles ────────────────────────────────────────────────
      // A per-frame REBUILD on the 4b/4c mould — reset, re-add every live line,
      // commit — so there is no slot to leak and nothing to reconcile: the
      // batch is a pure function of `speech.bubbles` on the step it is drawn.
      //
      // It is HERE rather than earlier because it reads FINISHED positions and
      // FINISHED moods. `manager.update` (step 2) is what settles where an
      // actor is standing, and the ambient source (step 2b — the population
      // director, or the spawner) is what settles who is alarmed, seated or
      // fleeing; a bubble opened before either would be anchored over the head
      // of somebody who has not moved yet and would quote a mood that is one
      // step stale. It draws no rng and writes no sim field, so like the blips
      // it can only sit between the last write and the input flush.
      if (speech && bubbles) {
        const p = player.position;
        speech.update(dt, p.x, p.y, p.z);
        bubbles.reset();
        const live = speech.bubbles;
        for (let i = 0; i < live.length; i++) {
          const b = live[i];
          bubbles.add(b.x, b.y, b.z, b.text, b.chars, b.alpha, b.cell, b.style);
        }
        bubbles.commit();
      }

      // ── 5d. the sound's SIM-COUPLED half ──────────────────────────────────
      // Footsteps, the idling engines, the rotor loop and where the siren is
      // standing. It is HERE, after speech's 5c, for the reason every other
      // step-5 entry is: it is a pure READ of a settled frame — finished
      // positions (step 2), a finished car (step 1), a finished drone (3c) —
      // it writes no sim field and it draws no rng.
      //
      // ⚠ ITS OTHER HALF IS IN `render`, AND THE SPLIT IS THE POINT. `sfx`'s
      // own ageing and every loop fade must keep running while an overlay
      // freezes this step; only the polling above may stop. See
      // `GameAudio.js`'s header, which states the rule and the failure it
      // prevents (a phone ringing over a pause menu, for ever).
      audio?.update(dt);

      emitHp();

      // 5b. HUD blips, ~8 Hz. A pure READ of state this step already finished:
      // it draws no random numbers, mutates no actor and is not read back, so
      // it can only sit between the last write and the input flush. Do not
      // promote it into the loop above — the minimap wants a settled frame.
      blipAcc += dt;
      if (blipAcc >= BLIPS.interval) {
        blipAcc = 0;
        emitBlips();
      }

      // 6. LAST: flush the edge-triggered key sets.
      input.update();
    },

    /**
     * THE DISPLAY-RATE HOOK, and this system had none until the sound arrived.
     * `Engine#_render` calls it every drawn frame with a TIME-SCALED delta —
     * which is exactly 0 while any of the five overlays holds the freeze
     * (`ui/hud/freezeOwner.js`), and that is precisely the case it exists for:
     * `onFixed` is not called at all then, so voice reaping and loop fades
     * would stop dead with the simulation.
     *
     * ⚠ NOTHING ELSE MAY MOVE IN HERE. Every other system in this file is on
     * the fixed step, and a second place that advances game state — at a rate
     * that changes with the display and with the perf governor's cap — is the
     * end of determinism and of the hero checksum. `GameAudio#render` writes no
     * sim field, draws no rng and reads its own real clock.
     *
     * @param {number} dt Seconds since the last real render, time-scaled.
     * @param {number} alpha Interpolation factor. Unused — nothing here draws.
     */
    render(dt, alpha) {
      if (disposed) return;
      audio?.render(dt, alpha);
    },

    /** @returns {Object} Live counters for a debug read-out. */
    stats() {
      const sprite = spriteRenderer.stats();
      const rigStats = rigRenderer.stats();
      return {
        alive: manager.alive,
        kills,
        hp: player.hp,
        spawner: spawner.stats(),
        blood: blood.stats(),
        // Both are live: sprite creatures and rig creatures coexist. The rig
        // entry carries `partsPerType` — the draw budget made visible.
        renderers: { sprite, rig: rigStats },
        traffic: traffic ? traffic.stats() : null,
        driving: driving ? driving.stats() : null,
        carjack: carjack ? carjack.stats() : null,
        interiors: interiors ? interiors.stats() : null,
        shops: shops ? shops.stats() : null,
        wallet: wallet.stats(),
        weapons: weapons ? weapons.stats() : null,
        // Beside the magazine it feeds, because it is the same reading: what
        // the gun holds, and what is on its way to it.
        drone: drone ? drone.stats() : null,
        phone: phone ? phone.stats() : null,
        // Null on every level without a sound table, exactly like the four
        // content-gated rows around it. It carries the facade's own `stats()`
        // nested, so one read answers both "what did the wiring ask for" and
        // "what did the mixer do with it".
        audio: audio ? audio.stats() : null,
        // What is on air and how far into it the world is — a reading nothing
        // else can answer, because the model is the only thing holding that
        // clock. Null on every level without a radio table.
        radio: radio ? radio.stats() : null,
        population: population ? population.stats() : null,
        factions: fac ? fac.state.stats() : null,
        wanted: wanted ? wanted.stats() : null,
        breakables: breakables ? breakables.stats() : null,
        vehicles: vehicles ? vehicles.stats() : null,
        missions: missions ? missions.stats() : null,
        speech: speech ? speech.stats() : null,
        bubbles: bubbles ? bubbles.stats() : null,
        markers: markerBatch ? markerBatch.stats() : null,
        census: manager.stats(),
      };
    },

    /** Idempotent — safe from both `level.dispose()` and `engine.dispose()`. */
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const off of offs) off?.();
      // ⚠ THE SOUND FIRST OF EVERYTHING, before a single system below can
      // publish on its way out. It holds fifteen bus subscriptions and it is
      // wired to channels that FIRE DURING A TEARDOWN — `interiors.dispose()`
      // aborts a door transition (`'screen:fade'`), `shops.dispose()` closes
      // the till, `phone.dispose()` cancels a call, `wanted.dispose()` stands
      // the response down (`'wanted:level'`) — so anything later would leave a
      // siren or a door slam playing over a level that no longer exists. Its
      // own `dispose()` drops the subscriptions and then releases every loop it
      // is holding, in that order.
      audio?.dispose();
      audio = null;
      // The facade next, and only after the wiring: it owns the `AudioContext`
      // and the node pool, so tearing it down first would leave the wiring
      // calling into a dead graph for the rest of this teardown.
      sfx?.dispose();
      sfx = null;
      // ...and the two combat links the sound chained onto, taken out of the
      // chain BEFORE the teardown nulls the five `manager.on*` callbacks below
      // — a restore written into a handler nobody will call again is a restore
      // that silently deletes the faction director's crime taxonomy.
      unchainCombat?.();
      unchainCombat = null;
      // THE DOOR FIRST OF ALL, before `carjack` and long before `driving`: its
      // `abort()` is the only thing that can bring a player back out from under
      // the city, it borrows both of those systems to decide whether it is even
      // allowed to run, and it puts the player's outdoor collision grid and
      // height function back — which every teardown below assumes is already
      // the case.
      interiors?.dispose();
      // ...and the shop with it, in the same breath and for two reasons. Its
      // whole lifecycle hangs off `interiors.plan`, which the line above has
      // just dropped; and its `clear()` despawns its crowd through
      // `manager.onDespawn`, which it CHAINED at construction and restores here
      // — so it must run long before the teardown nulls that callback, or the
      // restore writes into a handler nobody will ever call again.
      shops?.dispose();
      shops = null;
      // The vehicle sequence next: it may be holding a car, a pin and somebody
      // else's occupants, and its unwind needs both the director and the
      // population director still alive to hand them back to.
      carjack?.dispose();
      // The mission director next, and it MUST come before `driving`: its
      // escort may be riding in the player's car, and handing those passengers
      // back out of the door needs the driving body still alive.
      missions?.dispose();
      missions = null;
      markerBatch?.dispose();
      markerBatch = null;
      // The door rings own a texture and a material of their own (a second
      // batch, not a second view of the mission one), so they are released here
      // beside it rather than left to the registry's safety net.
      doorMarkers?.dispose();
      doorMarkers = null;
      // The talking next, and it goes EARLY for the same reason the shop does:
      // it hangs off the despawn door (`speech?.onSlotFreed`), so it has to be
      // gone long before the teardown nulls `manager.onDespawn` and long before
      // `manager.dispose()`. It also reads the wanted level live, which is
      // stood down a few lines below. Its batch owns real GPU resources, so it
      // is released explicitly rather than left to the registry's safety net —
      // the same rule the door rings above follow. Both are nulled, so a second
      // `dispose()` is a pair of no-ops.
      speech?.dispose();
      speech = null;
      bubbles?.dispose();
      bubbles = null;
      // The drone in the same breath, and for the same two reasons stated one
      // line apart above. It holds a BUS SUBSCRIPTION (`'player:ammo'` — the
      // whole trigger), which has to be dropped before a late publish can reach
      // a system on its way out; and its mesh owns real GPU resources of its
      // own (two geometries, a material and a third `MissionMarkerBatch`), so
      // it is released explicitly rather than left to the registry's safety
      // net. Both are nulled, so a second `dispose()` is a pair of no-ops — the
      // rule the door rings and the speech batch above both follow.
      // ⚠ THE PHONE GOES FIRST, BEFORE THE DRONE IT TALKS TO. It can have a
      // PANEL on screen over a world that is going away — the shop's
      // unrecoverable lesson — and it can have a CALL up, and both of those are
      // frozen overlays holding a claim on the time scale. Its `dispose()`
      // closes and cancels before it unsubscribes, so the HUD is answered on
      // the way out instead of being stranded over a dead level with nothing
      // left to reply to it. It also holds a `'mission:offer'` subscription
      // that must be dropped before a late drain can reach it.
      phone?.dispose();
      phone = null;
      drone?.dispose();
      drone = null;
      droneMesh?.dispose();
      droneMesh = null;
      // The radio with them — a content model like the two above it, and safe
      // here because the ONLY thing that ever called into it was `audio`, which
      // went first of everything and took the `'radio:track'` subscription with
      // it. There is nothing left that could tell it the time or hear it answer.
      radio?.dispose();
      radio = null;
      // The wanted system next: it drops the `'crime'` subscription and stands
      // its units down while the manager, the population and the traffic
      // director it borrows them from are all still alive.
      wanted?.dispose();
      // The breakables hold no GPU resource of their own — they write into the
      // LEVEL's prop batches — so this only has to stop the writes before the
      // level frees them.
      breakables?.dispose();
      // The vehicle damage before either of the systems it writes into: it
      // holds part poses on the FLEET and slot state in the DIRECTOR, and both
      // are torn down below. Its fx batch owns real GPU resources, so it is
      // released explicitly rather than left to the registry's safety net —
      // same rule the door rings follow.
      vehicles?.dispose();
      vehicles = null;
      vehicleFx?.dispose();
      vehicleFx = null;
      // Driving next: it hands the player's car back to the director, which
      // then frees every fleet handle it owns (including that one).
      driving?.dispose();
      // The director before the traffic it seats people in, so its teardown
      // still has a live pool to release them from.
      population?.dispose();
      population = null;
      traffic?.dispose();
      manager.onSpawn = null;
      manager.onDespawn = null;
      manager.onKill = null;
      manager.onFire = null;
      manager.onDamage = null;
      manager.dispose();
      spriteRenderer.dispose();
      rigRenderer.dispose();
      tracers.dispose();
      blood.dispose();
      blobs.dispose();
      player.dispose();
      // The wallet last of the systems: it holds no resource, but freezing the
      // figure stops a late emit repainting a HUD that is on its way out.
      wallet.dispose();
      // ...and the bar beside it, same shape and same reason: no GPU resource,
      // but a frozen model cannot publish a late readout into a HUD that is on
      // its way out. `player.speedMul` is left wherever the last step put it —
      // the actor above has already been disposed and nothing will read it
      // again.
      stamina.dispose();
      // ...and the magazine with it, for the identical reason and with the
      // identical shape: it holds no GPU resource, and disposing it drops its
      // two bus subscriptions and freezes the figures so a late `'player:ammo'`
      // cannot repaint a HUD on its way out. It is safe THIS late — `shops`
      // went down at the top of this teardown, so no verb can reach a disposed
      // model through `ctx.loadout`.
      weapons?.dispose();
      rig.dispose();
      drag.dispose();
      input.dispose();
      ownedGrid = null;
    },
  };

  return sys;
}

export { FIRE as GAMEPLAY_FIRE };
